import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  getAdminDashboardOverview,
  getAdminUserAnalytics,
  getAdminCampaignAnalytics,
  getAdminInvitationAnalytics,
  getAdminCreators,
  getAdminBrands,
  getAdminRejectedCreators,
  getAdminPendingPayments,
  getAdminPaymentHistory,
  markCreatorPaid,
  getAdminExportUrl,
  getAdminDatabaseCollections,
  browseAdminCollection,
  getAdminRevenueDashboard,
  getAdminCampaignROI,
  getCampaignContentRejections,
  getAllContentRejections,
  getAdminPlatformHealth,
  getAdminAlerts,
  sendAdminBulkEmail,
  getAdminBulkEmailHistory,
  adminCampaignControl,
  processAdminRefund,
  getAdminPendingRefunds,
  getRazorpayBalance,
  adminUserAction,
  getAdminSuspendedUsers,
  isAdminLoggedIn,
  adminLogout,
  getAdminInfo,
  // Performance Hub admin analytics (additive — new isolated backend module)
  perfHubAdminOverview,
  perfHubAdminCampaigns,
  perfHubAdminCampaignDetail,
  perfHubAdminUnconnected,
  perfHubAdminBrands,
  perfHubAdminAbandonedCheckouts,
  perfHubAdminExport
} from '../api/api';
import './AdminDashboard.css';
import './AdminPerformanceHub.css';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [adminInfo, setAdminInfo] = useState(null);
  const [activeSection, setActiveSection] = useState(searchParams.get('section') || 'overview');
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data states
  const [overview, setOverview] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [campaignAnalytics, setCampaignAnalytics] = useState(null);
  const [invitationAnalytics, setInvitationAnalytics] = useState(null);
  const [creators, setCreators] = useState({ list: [], pagination: null });
  const [brands, setBrands] = useState({ list: [], pagination: null });
  const [rejectedCreators, setRejectedCreators] = useState([]);
  const [pendingPayments, setPendingPayments] = useState({ campaigns: [], summary: null });
  const [paymentHistory, setPaymentHistory] = useState({ transactions: [], total: 0 });
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('creators'); // 'brands' | 'creators'
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('pending'); // 'pending' | 'completed' | 'failed'

  // Filters - separate input state from applied search state
  const [creatorSearchInput, setCreatorSearchInput] = useState('');
  const [brandSearchInput, setBrandSearchInput] = useState('');
  const [creatorSearchApplied, setCreatorSearchApplied] = useState('');
  const [brandSearchApplied, setBrandSearchApplied] = useState('');
  const [creatorPage, setCreatorPage] = useState(1);
  const [brandPage, setBrandPage] = useState(1);

  // Payment modal
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [paymentForm, setPaymentForm] = useState({ utr_reference: '', payment_method: 'upi', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Database Explorer
  const [dbCollections, setDbCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState('');
  const [dbDocuments, setDbDocuments] = useState([]);
  const [dbPagination, setDbPagination] = useState(null);
  const [dbSearchInput, setDbSearchInput] = useState('');
  const [dbSearchApplied, setDbSearchApplied] = useState('');
  const [dbSearchField, setDbSearchField] = useState('');
  const [dbPage, setDbPage] = useState(1);
  const [dbSearchableFields, setDbSearchableFields] = useState([]);
  const [expandedDoc, setExpandedDoc] = useState(null);

  // Revenue Dashboard
  const [revenueData, setRevenueData] = useState(null);

  // Campaign ROI
  const [campaignROI, setCampaignROI] = useState(null);
  const [showROIModal, setShowROIModal] = useState(false);
  const [selectedROICampaign, setSelectedROICampaign] = useState(null);
  const [roiCampaignId, setRoiCampaignId] = useState('');

  // Content Rejections
  const [contentRejections, setContentRejections] = useState([]);
  const [selectedCampaignRejections, setSelectedCampaignRejections] = useState(null);

  // Platform Health
  const [platformHealth, setPlatformHealth] = useState(null);

  // Alerts
  const [alerts, setAlerts] = useState({ alerts: [], summary: null });

  // Bulk Email
  const [bulkEmailForm, setBulkEmailForm] = useState({ recipient_type: 'creators', subject: '', message: '' });
  const [bulkEmailHistory, setBulkEmailHistory] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Campaign Controls
  const [campaignControlForm, setCampaignControlForm] = useState({ action: '', reason: '', new_deadline: '' });
  const [showCampaignControlModal, setShowCampaignControlModal] = useState(false);
  const [selectedCampaignForControl, setSelectedCampaignForControl] = useState(null);

  // Refunds
  const [pendingRefunds, setPendingRefunds] = useState({ refunds: [], completed_refunds: [], summary: {} });
  const [refundForm, setRefundForm] = useState({ campaign_id: '', amount: '', reason: '' });
  const [showRefundModal, setShowRefundModal] = useState(false);
  const [refundFilter, setRefundFilter] = useState('in_progress'); // 'in_progress' | 'awaiting' | 'failed' | 'completed'
  // Razorpay merchant balance (advisory; null until first fetch)
  const [razorpayBalance, setRazorpayBalance] = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // User Management
  const [suspendedUsers, setSuspendedUsers] = useState([]);
  const [userActionForm, setUserActionForm] = useState({ action: '', reason: '' });
  const [showUserActionModal, setShowUserActionModal] = useState(false);
  const [selectedUserForAction, setSelectedUserForAction] = useState(null);

  // ============================================================
  // Performance Hub admin section (additive — read-only analytics)
  // ============================================================
  // All state below is scoped to the new 'performance-hub' section. It does
  // not influence any pre-existing section's data flow. The backend lives in
  // performance_hub/admin_routes.py and is isolated from admin_dashboard.
  const [phGranularity, setPhGranularity] = useState('day');
  const [phOverview, setPhOverview] = useState(null);
  const [phCampaigns, setPhCampaigns] = useState({ list: [], pagination: null });
  const [phCampaignStatusFilter, setPhCampaignStatusFilter] = useState('all');
  const [phCampaignSearchInput, setPhCampaignSearchInput] = useState('');
  const [phCampaignSearchApplied, setPhCampaignSearchApplied] = useState('');
  const [phCampaignPage, setPhCampaignPage] = useState(1);
  const [phStaleCampaignsOnly, setPhStaleCampaignsOnly] = useState(false);
  const [phFilterBrandId, setPhFilterBrandId] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('ph_brand') || '';
    } catch {
      return '';
    }
  });
  const [phFilterCampaignId, setPhFilterCampaignId] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('ph_campaign') || '';
    } catch {
      return '';
    }
  });
  const [phCampaignDetail, setPhCampaignDetail] = useState(null);
  const [phCampaignDetailLoading, setPhCampaignDetailLoading] = useState(false);
  const [phUnconnected, setPhUnconnected] = useState({
    rows: [],
    pagination: null,
    summary: null,
    errorBreakdown: []
  });
  const [phUnconnectedStatus, setPhUnconnectedStatus] = useState('all_unconnected');
  const [phUnconnectedSearchInput, setPhUnconnectedSearchInput] = useState('');
  const [phUnconnectedSearchApplied, setPhUnconnectedSearchApplied] = useState('');
  const [phUnconnectedPage, setPhUnconnectedPage] = useState(1);
  const [phBrands, setPhBrands] = useState({ list: [], pagination: null, summary: null });
  const [phBrandPlanFilter, setPhBrandPlanFilter] = useState('all');
  const [phBrandRenewWithinDays, setPhBrandRenewWithinDays] = useState(30);
  const [phBrandNearCapOnly, setPhBrandNearCapOnly] = useState(false);
  const [phBrandSearchInput, setPhBrandSearchInput] = useState('');
  const [phBrandSearchApplied, setPhBrandSearchApplied] = useState('');
  const [phBrandPage, setPhBrandPage] = useState(1);
  const [phAbandoned, setPhAbandoned] = useState({ orders: [], total: 0 });
  /** Which sub-tab is active inside the Performance Hub section. */
  const [phSubTab, setPhSubTab] = useState(() => {
    try {
      const s = new URLSearchParams(window.location.search).get('ph_sub');
      return ['overview', 'campaigns', 'unconnected', 'brands', 'abandoned'].includes(s)
        ? s
        : 'overview';
    } catch {
      return 'overview';
    }
  });

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      // First check if token exists
      const hasToken = isAdminLoggedIn();
      
      if (!hasToken) {
        // No token - redirect immediately using window.location
        window.location.href = '/admin/login';
        return;
      }
      
      // Verify token is still valid by making a test API call
      try {
        await getAdminDashboardOverview();
        setIsAuthenticated(true);
        setAdminInfo(getAdminInfo());
      } catch (error) {
        // Token is invalid/expired, clear and redirect
        adminLogout();
        window.location.href = '/admin/login';
      }
    };
    
    checkAuth();
  }, []);

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
  };

  // Update URL when section changes (preserve Performance Hub deep-link params)
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('section', activeSection);
        if (activeSection !== 'performance-hub') {
          p.delete('ph_sub');
          p.delete('ph_brand');
          p.delete('ph_campaign');
        }
        return p;
      },
      { replace: true }
    );
  }, [activeSection, setSearchParams]);

  useEffect(() => {
    if (activeSection !== 'performance-hub') return;
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        p.set('section', 'performance-hub');
        p.set('ph_sub', phSubTab);
        if (phFilterBrandId) p.set('ph_brand', phFilterBrandId);
        else p.delete('ph_brand');
        if (phFilterCampaignId) p.set('ph_campaign', phFilterCampaignId);
        else p.delete('ph_campaign');
        return p;
      },
      { replace: true }
    );
  }, [activeSection, phSubTab, phFilterBrandId, phFilterCampaignId, setSearchParams]);

  useEffect(() => {
    if (activeSection !== 'performance-hub') return;
    const sub = searchParams.get('ph_sub');
    if (sub && ['overview', 'campaigns', 'unconnected', 'brands', 'abandoned'].includes(sub)) {
      setPhSubTab((cur) => (cur !== sub ? sub : cur));
    }
    const pb = searchParams.get('ph_brand') || '';
    setPhFilterBrandId((cur) => (cur !== pb ? pb : cur));
    const pc = searchParams.get('ph_campaign') || '';
    setPhFilterCampaignId((cur) => (cur !== pc ? pc : cur));
  }, [searchParams, activeSection]);

  const downloadPhCsv = useCallback(
    async (kind, extra = {}) => {
      try {
        const params = { period, granularity: phGranularity, ...extra };
        const res = await perfHubAdminExport(kind, params);
        const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `performance-hub-${kind}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('PH CSV export failed', err);
        alert(err.response?.data?.detail || 'Export failed.');
      }
    },
    [period, phGranularity]
  );

  // Fetch data based on active section
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      switch (activeSection) {
        case 'overview':
          const overviewRes = await getAdminDashboardOverview();
          if (overviewRes.data.success) setOverview(overviewRes.data);
          break;

        case 'users':
        case 'creators':
        case 'brands':
          const userRes = await getAdminUserAnalytics({ period });
          if (userRes.data.success) setUserAnalytics(userRes.data);
          break;

        case 'campaigns':
          const campaignRes = await getAdminCampaignAnalytics({ period });
          if (campaignRes.data.success) setCampaignAnalytics(campaignRes.data);
          break;

        case 'invitations':
          const invRes = await getAdminInvitationAnalytics({ period });
          if (invRes.data.success) setInvitationAnalytics(invRes.data);
          break;

        case 'creators-list':
          const creatorsRes = await getAdminCreators({ page: creatorPage, limit: 50, search: creatorSearchApplied });
          if (creatorsRes.data.success) setCreators({ list: creatorsRes.data.creators, pagination: creatorsRes.data.pagination });
          break;

        case 'brands-list':
          const brandsRes = await getAdminBrands({ page: brandPage, limit: 50, search: brandSearchApplied });
          if (brandsRes.data.success) setBrands({ list: brandsRes.data.brands, pagination: brandsRes.data.pagination });
          break;

        case 'communications':
          const rejectedRes = await getAdminRejectedCreators({ period });
          if (rejectedRes.data.success) setRejectedCreators(rejectedRes.data.creators);
          break;

        case 'payments':
          const [pendingRes, historyRes, paymentsRefundsRes] = await Promise.all([
            getAdminPendingPayments({ period }),
            getAdminPaymentHistory({ period, limit: 50 }),
            getAdminPendingRefunds() // Also fetch refunds for Brand Payments tab
          ]);
          if (pendingRes.data.success) setPendingPayments({ campaigns: pendingRes.data.campaigns, summary: pendingRes.data.summary });
          if (historyRes.data.success) setPaymentHistory({ transactions: historyRes.data.transactions, total: historyRes.data.total, totalAmount: historyRes.data.total_amount });
          if (paymentsRefundsRes.data.success) {
            setPendingRefunds({
              refunds: paymentsRefundsRes.data.refunds || [],
              completed_refunds: paymentsRefundsRes.data.completed_refunds || [],
              summary: paymentsRefundsRes.data.summary || {}
            });
          }
          break;

        case 'database':
          // Fetch collections list
          const collectionsRes = await getAdminDatabaseCollections();
          if (collectionsRes.data.success) {
            setDbCollections(collectionsRes.data.collections);
            // If a collection is selected, fetch its documents
            if (selectedCollection) {
              const docsRes = await browseAdminCollection(selectedCollection, {
                page: dbPage,
                limit: 20,
                search: dbSearchApplied,
                search_field: dbSearchField || undefined
              });
              if (docsRes.data.success) {
                setDbDocuments(docsRes.data.documents);
                setDbPagination(docsRes.data.pagination);
                setDbSearchableFields(docsRes.data.searchable_fields);
              }
            }
          }
          break;

        case 'revenue':
          const revenueRes = await getAdminRevenueDashboard({ period });
          if (revenueRes.data.success) setRevenueData(revenueRes.data);
          break;

        case 'roi':
          const roiRes = await getAdminCampaignROI({ period, campaign_id: roiCampaignId || undefined });
          if (roiRes.data.success) setCampaignROI(roiRes.data);
          break;

        case 'content-rejections':
          const rejectionsRes = await getAllContentRejections({ period, status_filter: 'rejected' });
          if (rejectionsRes.data.success) setContentRejections(rejectionsRes.data.reviews);
          break;

        case 'platform-health':
          const [healthRes, alertsRes] = await Promise.all([
            getAdminPlatformHealth(),
            getAdminAlerts()
          ]);
          if (healthRes.data.success) setPlatformHealth(healthRes.data);
          if (alertsRes.data.success) setAlerts(alertsRes.data);
          break;

        case 'bulk-email':
          const emailHistoryRes = await getAdminBulkEmailHistory();
          if (emailHistoryRes.data.success) setBulkEmailHistory(emailHistoryRes.data.logs);
          break;

        case 'campaign-controls':
          // Re-fetch campaigns for controls
          const controlCampaignsRes = await getAdminCampaignAnalytics({ period });
          if (controlCampaignsRes.data.success) setCampaignAnalytics(controlCampaignsRes.data);
          break;

        case 'refunds':
          const refundsRes = await getAdminPendingRefunds();
          if (refundsRes.data.success) {
            setPendingRefunds({
              refunds: refundsRes.data.refunds || [],
              completed_refunds: refundsRes.data.completed_refunds || [],
              summary: refundsRes.data.summary || {}
            });
          }
          break;

        case 'user-management':
          const suspendedRes = await getAdminSuspendedUsers();
          if (suspendedRes.data.success) setSuspendedUsers(suspendedRes.data.users);
          break;

        case 'performance-hub': {
          const phParams = { period, granularity: phGranularity };
          if (phSubTab === 'overview') {
            const ovRes = await perfHubAdminOverview(phParams);
            if (ovRes.data.success) setPhOverview(ovRes.data);
          } else if (phSubTab === 'campaigns') {
            const cRes = await perfHubAdminCampaigns({
              ...phParams,
              status: phCampaignStatusFilter,
              search: phCampaignSearchApplied || undefined,
              brand_id: phFilterBrandId || undefined,
              stale_only: phStaleCampaignsOnly,
              page: phCampaignPage,
              limit: 25
            });
            if (cRes.data.success) {
              setPhCampaigns({ list: cRes.data.campaigns, pagination: cRes.data.pagination });
            }
          } else if (phSubTab === 'unconnected') {
            const uRes = await perfHubAdminUnconnected({
              ...phParams,
              status: phUnconnectedStatus,
              search: phUnconnectedSearchApplied || undefined,
              campaign_id: phFilterCampaignId || undefined,
              brand_id: phFilterBrandId || undefined,
              page: phUnconnectedPage,
              limit: 50
            });
            if (uRes.data.success) {
              setPhUnconnected({
                rows: uRes.data.rows,
                pagination: uRes.data.pagination,
                summary: uRes.data.summary,
                errorBreakdown: uRes.data.error_reason_breakdown || []
              });
            }
          } else if (phSubTab === 'brands') {
            const bRes = await perfHubAdminBrands({
              ...phParams,
              plan: phBrandPlanFilter,
              search: phBrandSearchApplied || undefined,
              page: phBrandPage,
              limit: 25,
              renew_within_days: phBrandRenewWithinDays,
              near_cap_only: phBrandNearCapOnly
            });
            if (bRes.data.success) {
              setPhBrands({
                list: bRes.data.brands,
                pagination: bRes.data.pagination,
                summary: bRes.data.summary
              });
            }
          } else if (phSubTab === 'abandoned') {
            const aRes = await perfHubAdminAbandonedCheckouts({ ...phParams, limit: 200 });
            if (aRes.data.success) {
              setPhAbandoned({ orders: aRes.data.orders || [], total: aRes.data.total || 0 });
            }
          }
          break;
        }

        default:
          break;
      }
    } catch (err) {
      console.error('Error fetching admin data:', err);
      setError('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [activeSection, period, creatorPage, brandPage, creatorSearchApplied, brandSearchApplied, selectedCollection, dbPage, dbSearchApplied, dbSearchField, roiCampaignId, phSubTab, phGranularity, phCampaignStatusFilter, phCampaignSearchApplied, phCampaignPage, phStaleCampaignsOnly, phFilterBrandId, phFilterCampaignId, phUnconnectedStatus, phUnconnectedSearchApplied, phUnconnectedPage, phBrandPlanFilter, phBrandRenewWithinDays, phBrandNearCapOnly, phBrandSearchApplied, phBrandPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch Razorpay merchant balance whenever the admin lands on the Refunds tab.
  // This is advisory only — used to warn before a retry that the balance is
  // too low, so admins know to top up or process manually instead of clicking
  // Retry repeatedly. Best-effort: a fetch failure is silently ignored.
  const fetchRazorpayBalance = useCallback(async () => {
    try {
      setBalanceLoading(true);
      const res = await getRazorpayBalance();
      setRazorpayBalance(res.data || null);
    } catch (err) {
      setRazorpayBalance({ fetched: false, error: 'Balance check unavailable.' });
    } finally {
      setBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeSection === 'refunds') {
      fetchRazorpayBalance();
    }
  }, [activeSection, fetchRazorpayBalance]);

  // Open the Record Payment modal for ONE installment (a single weekly_fixed
  // week, or the performance bonus). The installment carries its own
  // net_payable / type / week_number so the modal stays generic.
  const handlePayCreator = (campaign, creator, installment) => {
    setSelectedCreator({ ...creator, campaign, installment });
    setPaymentForm({ utr_reference: '', payment_method: 'upi', notes: '' });
    setShowPaymentModal(true);
  };

  const handleSubmitPayment = async (e) => {
    e.preventDefault();
    if (!paymentForm.utr_reference.trim()) {
      alert('Please enter UTR/Transaction Reference');
      return;
    }

    const installment = selectedCreator.installment;
    if (!installment) {
      alert('No installment selected');
      return;
    }

    setSubmitting(true);
    try {
      const response = await markCreatorPaid({
        campaign_id: selectedCreator.campaign?.campaign_id,
        creator_id: selectedCreator.creator_id,
        amount: installment.net_payable,
        utr_reference: paymentForm.utr_reference.trim(),
        payment_method: paymentForm.payment_method,
        notes: paymentForm.notes.trim() || null,
        installment_type: installment.type,
        week_number: installment.week_number,
      });

      if (response.data.success) {
        setSuccessMessage(
          `${installment.label}: ₹${installment.net_payable.toLocaleString()} recorded for ${selectedCreator.name}.`
        );
        setShowPaymentModal(false);
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to mark payment');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const handleExport = (type) => {
    window.open(getAdminExportUrl(type), '_blank');
  };

  // Navigation items organized by category
  const navItems = [
    { id: 'overview', label: 'Dashboard', icon: '📊', category: 'main' },
    { id: 'revenue', label: 'Revenue', icon: '💵', category: 'insights' },
    { id: 'roi', label: 'Campaign ROI', icon: '📈', category: 'insights' },
    { id: 'platform-health', label: 'Platform Health', icon: '🏥', category: 'insights' },
    { id: 'users', label: 'User Analytics', icon: '👥', category: 'analytics' },
    { id: 'creators-list', label: 'All Creators', icon: '🎨', category: 'analytics' },
    { id: 'brands-list', label: 'All Brands', icon: '🏢', category: 'analytics' },
    { id: 'campaigns', label: 'Campaigns', icon: '🎯', category: 'analytics' },
    { id: 'invitations', label: 'Invitations', icon: '📧', category: 'analytics' },
    { id: 'content-rejections', label: 'Content Reviews', icon: '📝', category: 'analytics' },
    { id: 'payments', label: 'Payments', icon: '💰', category: 'actions' },
    { id: 'refunds', label: 'Refunds', icon: '↩️', category: 'actions' },
    { id: 'bulk-email', label: 'Bulk Email', icon: '📨', category: 'actions' },
    { id: 'campaign-controls', label: 'Campaign Controls', icon: '⚙️', category: 'actions' },
    { id: 'user-management', label: 'User Management', icon: '👤', category: 'actions' },
    { id: 'communications', label: 'Communications', icon: '📞', category: 'actions' },
    { id: 'database', label: 'Database', icon: '🗄️', category: 'tools' },
    // Performance Hub admin section (additive). Lives under its own
    // 'performance-hub' category so the sidebar groups it visually apart
    // from the main campaign-flow admin pages.
    { id: 'performance-hub', label: 'Performance Hub', icon: '🎯', category: 'performance-hub' },
  ];

  // Handler functions for new features
  const handleSendBulkEmail = async (e) => {
    e.preventDefault();
    if (!bulkEmailForm.subject.trim() || !bulkEmailForm.message.trim()) {
      alert('Please fill in subject and message');
      return;
    }
    setSendingEmail(true);
    try {
      const response = await sendAdminBulkEmail(bulkEmailForm);
      if (response.data.success) {
        setSuccessMessage(`Email sent to ${response.data.stats.sent} recipients!`);
        setBulkEmailForm({ recipient_type: 'creators', subject: '', message: '' });
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleCampaignControl = async (e) => {
    e.preventDefault();
    if (!campaignControlForm.action) {
      alert('Please select an action');
      return;
    }
    setSubmitting(true);
    try {
      const response = await adminCampaignControl(selectedCampaignForControl.campaign_id, campaignControlForm);
      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setShowCampaignControlModal(false);
        setCampaignControlForm({ action: '', reason: '', new_deadline: '' });
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to perform action');
    } finally {
      setSubmitting(false);
    }
  };

  const handleProcessRefund = async (e) => {
    e.preventDefault();
    if (!refundForm.campaign_id || !refundForm.reason) {
      alert('Please fill in campaign and reason');
      return;
    }
    setSubmitting(true);
    try {
      const response = await processAdminRefund({
        campaign_id: refundForm.campaign_id,
        amount: refundForm.amount ? parseFloat(refundForm.amount) : undefined,
        reason: refundForm.reason
      });
      if (response.data.success) {
        setSuccessMessage(`Refund of ₹${response.data.details.refund_amount.toLocaleString()} initiated!`);
        setShowRefundModal(false);
        setRefundForm({ campaign_id: '', amount: '', reason: '' });
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      const errorDetail = err.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? JSON.stringify(errorDetail) 
        : (errorDetail || 'Failed to process refund');
      alert(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUserAction = async (e) => {
    e.preventDefault();
    if (!userActionForm.action) {
      alert('Please select an action');
      return;
    }
    setSubmitting(true);
    try {
      const response = await adminUserAction(selectedUserForAction.type, selectedUserForAction.id, userActionForm);
      if (response.data.success) {
        setSuccessMessage(response.data.message);
        setShowUserActionModal(false);
        setUserActionForm({ action: '', reason: '' });
        fetchData();
        setTimeout(() => setSuccessMessage(''), 5000);
      }
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to perform action');
    } finally {
      setSubmitting(false);
    }
  };

  const openUserActionModal = (user, type) => {
    setSelectedUserForAction({ ...user, type });
    setUserActionForm({ action: '', reason: '' });
    setShowUserActionModal(true);
  };

  const openCampaignControlModal = (campaign) => {
    setSelectedCampaignForControl(campaign);
    setCampaignControlForm({ action: '', reason: '', new_deadline: '' });
    setShowCampaignControlModal(true);
  };

  const fetchCampaignRejections = async (campaignId) => {
    try {
      const response = await getCampaignContentRejections(campaignId);
      if (response.data.success) {
        setSelectedCampaignRejections(response.data);
      }
    } catch (err) {
      alert('Failed to load rejection details');
    }
  };

  const openROIDetailModal = (campaign) => {
    setSelectedROICampaign(campaign);
    setShowROIModal(true);
  };

  // Don't render dashboard until authenticated
  if (!isAuthenticated) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Verifying authentication...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/logo.png" alt="Milan AI" className="logo-img" />
            <div className="logo-text">
              <span className="brand-name">Milan AI</span>
              <span className="admin-label">Admin Panel</span>
            </div>
          </div>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-category">
            <span className="category-label">Overview</span>
            {navItems.filter(i => i.category === 'main').map(item => (
              <button
                key={item.id}
                className={`admin-nav-item ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => setActiveSection(item.id)}
              >
                <span className="admin-nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </div>
          <div className="nav-category">
            <span className="category-label">Insights</span>
            {navItems.filter(i => i.category === 'insights').map(item => (
              <button
                key={item.id}
                className={`admin-nav-item ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => setActiveSection(item.id)}
              >
                <span className="admin-nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </div>
          <div className="nav-category">
            <span className="category-label">Analytics</span>
            {navItems.filter(i => i.category === 'analytics').map(item => (
              <button
                key={item.id}
                className={`admin-nav-item ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => setActiveSection(item.id)}
              >
                <span className="admin-nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </div>
          <div className="nav-category">
            <span className="category-label">Actions</span>
            {navItems.filter(i => i.category === 'actions').map(item => (
              <button
                key={item.id}
                className={`admin-nav-item ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => setActiveSection(item.id)}
              >
                <span className="admin-nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </div>
          <div className="nav-category">
            <span className="category-label">Tools</span>
            {navItems.filter(i => i.category === 'tools').map(item => (
              <button
                key={item.id}
                className={`admin-nav-item ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => setActiveSection(item.id)}
              >
                <span className="admin-nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </div>
          {/* Performance Hub admin (additive category — completely separate
              from main campaign-flow admin sections above). */}
          <div className="nav-category">
            <span className="category-label">
              Performance Hub <span className="ph-admin-live-pill" aria-label="Live feature">LIVE</span>
            </span>
            {navItems.filter(i => i.category === 'performance-hub').map(item => (
              <button
                key={item.id}
                className={`admin-nav-item ${activeSection === item.id ? 'active' : ''}`}
                onClick={() => setActiveSection(item.id)}
              >
                <span className="admin-nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </div>
        </nav>
        
        {/* Admin Info & Logout */}
        <div className="sidebar-footer">
          <div className="admin-info">
            <div className="admin-avatar">
              {adminInfo?.name?.charAt(0)?.toUpperCase() || 'A'}
            </div>
            <div className="admin-details">
              <span className="admin-name">{adminInfo?.name || 'Admin'}</span>
              <span className="admin-role">{adminInfo?.role || 'admin'}</span>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            🚪 Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        {/* Header */}
        <header className="admin-content-header">
          <div className="header-left">
            <h1>{navItems.find(n => n.id === activeSection)?.label || 'Admin'}</h1>
          </div>
          <div className="header-right">
            <select value={period} onChange={(e) => setPeriod(e.target.value)} className="period-select">
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="quarter">Last 90 Days</option>
              <option value="year">Last Year</option>
            </select>
            <button onClick={fetchData} className="refresh-btn">🔄 Refresh</button>
          </div>
        </header>

        {/* Success Message */}
        {successMessage && <div className="success-banner">{successMessage}</div>}

        {/* Error Message */}
        {error && <div className="error-banner">{error} <button onClick={fetchData}>Retry</button></div>}

        {/* Loading */}
        {loading && (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        )}

        {/* Content */}
        {!loading && (
          <div className="admin-content">
            {/* OVERVIEW SECTION */}
            {activeSection === 'overview' && overview && (
              <div className="overview-section">
                {/* Quick Stats */}
                <div className="stats-grid">
                  <div className="stat-card primary">
                    <div className="stat-icon">👥</div>
                    <div className="stat-info">
                      <span className="stat-value">{overview.overview.total_users.total}</span>
                      <span className="stat-label">Total Users</span>
                      <span className="stat-sub">{overview.overview.total_users.creators} Creators • {overview.overview.total_users.brands} Brands</span>
                    </div>
                  </div>
                  <div className="stat-card success">
                    <div className="stat-icon">🎯</div>
                    <div className="stat-info">
                      <span className="stat-value">{overview.overview.campaigns.total}</span>
                      <span className="stat-label">Total Campaigns</span>
                      <span className="stat-sub">{overview.overview.campaigns.active} Active</span>
                    </div>
                  </div>
                  <div className="stat-card warning">
                    <div className="stat-icon">💰</div>
                    <div className="stat-info">
                      <span className="stat-value">₹{overview.overview.payments.total_escrow?.toLocaleString() || 0}</span>
                      <span className="stat-label">Total Escrow</span>
                      <span className="stat-sub">₹{overview.overview.payments.total_paid?.toLocaleString() || 0} Paid</span>
                    </div>
                  </div>
                  <div className="stat-card info">
                    <div className="stat-icon">📧</div>
                    <div className="stat-info">
                      <span className="stat-value">
                        {(overview.overview.invitations.responded ?? 0) > 0
                          ? `${overview.overview.invitations.acceptance_rate}%`
                          : '—'}
                      </span>
                      <span className="stat-label">Acceptance Rate</span>
                      <span className="stat-sub">
                        {overview.overview.invitations.accepted}/{overview.overview.invitations.responded ?? 0} decided
                        {' · '}
                        {overview.overview.invitations.pending ?? 0} pending
                        {' · '}
                        {overview.overview.invitations.total} total
                      </span>
                    </div>
                  </div>
                </div>

                <div className="section-row">
                  <div className="section-card">
                    <h3>📈 Today's Activity</h3>
                    <div className="today-stats">
                      <div className="today-item">
                        <span className="today-value">{overview.overview.today.creators}</span>
                        <span className="today-label">New Creators</span>
                      </div>
                      <div className="today-item">
                        <span className="today-value">{overview.overview.today.brands}</span>
                        <span className="today-label">New Brands</span>
                      </div>
                      <div className="today-item">
                        <span className="today-value">{overview.overview.today.campaigns}</span>
                        <span className="today-label">New Campaigns</span>
                      </div>
                    </div>
                  </div>

                  <div className="section-card">
                    <h3>📱 Instagram Engagement</h3>
                    <div className="engagement-stats">
                      <div className="progress-stat">
                        <div className="progress-header">
                          <span>Instagram Connected</span>
                          <span>{overview.overview.creators_engagement.instagram_percentage}%</span>
                        </div>
                        <div className="progress-bar">
                          <div className="progress-fill" style={{ width: `${overview.overview.creators_engagement.instagram_percentage}%` }}></div>
                        </div>
                        <span className="progress-detail">{overview.overview.creators_engagement.instagram_connected} of {overview.overview.creators_engagement.total}</span>
                      </div>
                      <div className="progress-stat">
                        <div className="progress-header">
                          <span>Payment Details Added</span>
                          <span>{overview.overview.creators_engagement.payment_details_percentage}%</span>
                        </div>
                        <div className="progress-bar">
                          <div className="progress-fill success" style={{ width: `${overview.overview.creators_engagement.payment_details_percentage}%` }}></div>
                        </div>
                        <span className="progress-detail">{overview.overview.creators_engagement.payment_details_added} of {overview.overview.creators_engagement.total}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* This Week & Month */}
                <div className="section-row">
                  <div className="section-card small">
                    <h3>📅 This Week</h3>
                    <div className="period-stats">
                      <div><span className="num">{overview.overview.this_week.creators}</span> Creators</div>
                      <div><span className="num">{overview.overview.this_week.brands}</span> Brands</div>
                      <div><span className="num">{overview.overview.this_week.campaigns}</span> Campaigns</div>
                    </div>
                  </div>
                  <div className="section-card small">
                    <h3>📆 This Month</h3>
                    <div className="period-stats">
                      <div><span className="num">{overview.overview.this_month.creators}</span> Creators</div>
                      <div><span className="num">{overview.overview.this_month.brands}</span> Brands</div>
                      <div><span className="num">{overview.overview.this_month.campaigns}</span> Campaigns</div>
                    </div>
                  </div>
                  <div className="section-card small">
                    <h3>🎯 Campaign Status</h3>
                    <div className="period-stats">
                      <div><span className="num">{overview.overview.campaigns.active}</span> Active</div>
                      <div><span className="num">{overview.overview.campaigns.completed}</span> Completed</div>
                      <div><span className="num">{overview.overview.campaigns.pending}</span> Pending</div>
                      <div><span className="num">{overview.overview.campaigns.cancelled ?? 0}</span> Cancelled</div>
                    </div>
                  </div>
                </div>

                {/* Recent Activity */}
                <div className="section-row">
                  <div className="section-card">
                    <h3>🆕 Recent Creators</h3>
                    <div className="recent-list">
                      {overview.recent_activity.creators.map(c => (
                        <div key={c.id} className="recent-item">
                          <span className="recent-name">{c.name}</span>
                          <span className="recent-email">{c.email}</span>
                          <span className="recent-date">{formatDate(c.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="section-card">
                    <h3>🏢 Recent Brands</h3>
                    <div className="recent-list">
                      {overview.recent_activity.brands.map(b => (
                        <div key={b.id} className="recent-item">
                          <span className="recent-name">{b.name}</span>
                          <span className="recent-email">{b.email}</span>
                          <span className="recent-date">{formatDate(b.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* USER ANALYTICS SECTION */}
            {activeSection === 'users' && userAnalytics && (
              <div className="analytics-section">
                <div className="analytics-grid">
                  {/* Creators */}
                  <div className="analytics-card">
                    <h3>🎨 Creators ({period})</h3>
                    <div className="analytics-stats">
                      <div className="big-stat">
                        <span className="big-num">{userAnalytics.creators?.total || 0}</span>
                        <span className="big-label">Total Signups</span>
                      </div>
                      <div className="stat-row">
                        <span>Instagram Connected:</span>
                        <span className="stat-num">{userAnalytics.creators?.instagram_connected || 0} ({userAnalytics.creators?.instagram_percentage || 0}%)</span>
                      </div>
                    </div>
                    {userAnalytics.creators?.daily_breakdown?.length > 0 && (
                      <div className="mini-chart">
                        <h4>Daily Signups</h4>
                        <div className="chart-bars">
                          {userAnalytics.creators.daily_breakdown.slice(-14).map((d, i) => (
                            <div key={i} className="chart-bar-container">
                              <div 
                                className="chart-bar" 
                                style={{ height: `${Math.max(10, (d.count / Math.max(...userAnalytics.creators.daily_breakdown.map(x => x.count), 1)) * 100)}%` }}
                                title={`${d.date}: ${d.count}`}
                              ></div>
                              <span className="chart-label">{d.date.split('-')[2]}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {userAnalytics.creators?.niche_distribution?.length > 0 && (
                      <div className="distribution">
                        <h4>Top Niches</h4>
                        {userAnalytics.creators.niche_distribution.slice(0, 5).map((n, i) => (
                          <div key={i} className="dist-item">
                            <span>{n.niche}</span>
                            <span className="dist-count">{n.count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Brands */}
                  <div className="analytics-card">
                    <h3>🏢 Brands ({period})</h3>
                    <div className="analytics-stats">
                      <div className="big-stat">
                        <span className="big-num">{userAnalytics.brands?.total || 0}</span>
                        <span className="big-label">Total Signups</span>
                      </div>
                    </div>
                    {userAnalytics.brands?.daily_breakdown?.length > 0 && (
                      <div className="mini-chart">
                        <h4>Daily Signups</h4>
                        <div className="chart-bars">
                          {userAnalytics.brands.daily_breakdown.slice(-14).map((d, i) => (
                            <div key={i} className="chart-bar-container">
                              <div 
                                className="chart-bar brand" 
                                style={{ height: `${Math.max(10, (d.count / Math.max(...userAnalytics.brands.daily_breakdown.map(x => x.count), 1)) * 100)}%` }}
                                title={`${d.date}: ${d.count}`}
                              ></div>
                              <span className="chart-label">{d.date.split('-')[2]}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {userAnalytics.brands?.industry_distribution?.length > 0 && (
                      <div className="distribution">
                        <h4>Top Industries</h4>
                        {userAnalytics.brands.industry_distribution.slice(0, 5).map((ind, i) => (
                          <div key={i} className="dist-item">
                            <span>{ind.industry}</span>
                            <span className="dist-count">{ind.count}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Follower Distribution */}
                {userAnalytics.creators?.follower_distribution && (
                  <div className="section-card full-width">
                    <h3>📊 Creator Follower Distribution</h3>
                    <div className="follower-chart">
                      {userAnalytics.creators.follower_distribution.map((f, i) => (
                        <div key={i} className="follower-bar-container">
                          <div className="follower-info">
                            <span className="follower-range">{f.range}</span>
                            <span className="follower-count">{f.count}</span>
                          </div>
                          <div className="follower-bar-bg">
                            <div 
                              className="follower-bar-fill" 
                              style={{ width: `${(f.count / Math.max(...userAnalytics.creators.follower_distribution.map(x => x.count), 1)) * 100}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CREATORS LIST SECTION */}
            {activeSection === 'creators-list' && (
              <div className="list-section">
                <div className="list-controls">
                  <input
                    type="text"
                    placeholder="Search creators by name, email, or username..."
                    value={creatorSearchInput}
                    onChange={(e) => setCreatorSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setCreatorSearchApplied(creatorSearchInput);
                        setCreatorPage(1);
                      }
                    }}
                    className="search-input"
                  />
                  <button 
                    onClick={() => {
                      setCreatorSearchApplied(creatorSearchInput);
                      setCreatorPage(1);
                    }} 
                    className="search-btn"
                  >
                    🔍 Search
                  </button>
                  {creatorSearchApplied && (
                    <button 
                      onClick={() => {
                        setCreatorSearchInput('');
                        setCreatorSearchApplied('');
                        setCreatorPage(1);
                      }} 
                      className="clear-btn"
                    >
                      ✕ Clear
                    </button>
                  )}
                  <button onClick={() => handleExport('creators')} className="export-btn-inline">📥 Export CSV</button>
                </div>
                {creatorSearchApplied && (
                  <div className="search-info">
                    Showing results for: <strong>"{creatorSearchApplied}"</strong>
                  </div>
                )}

                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Instagram</th>
                        <th>Followers</th>
                        <th>Niche</th>
                        <th>Payment</th>
                        <th>Campaigns</th>
                        <th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creators.list.map(c => (
                        <tr key={c.id}>
                          <td className="name-cell">{c.name}</td>
                          <td className="email-cell">{c.email}</td>
                          <td>
                            {c.instagram_username ? (
                              <span className="instagram-badge">@{c.instagram_username}</span>
                            ) : (
                              <span className="not-connected">Not connected</span>
                            )}
                          </td>
                          <td>{c.instagram_followers?.toLocaleString() || 0}</td>
                          <td>{c.niche || '-'}</td>
                          <td>
                            {c.has_payment_details ? (
                              <span className="badge success">{c.payment_method?.toUpperCase()}</span>
                            ) : (
                              <span className="badge warning">Missing</span>
                            )}
                          </td>
                          <td>
                            <span className="campaign-stats">
                              <span className="accepted">{c.campaigns_accepted} ✓</span>
                              <span className="rejected">{c.campaigns_rejected} ✗</span>
                            </span>
                          </td>
                          <td>{formatDate(c.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {creators.pagination && (
                  <div className="pagination">
                    <button 
                      disabled={creatorPage <= 1} 
                      onClick={() => setCreatorPage(p => p - 1)}
                    >Previous</button>
                    <span>Page {creatorPage} of {creators.pagination.pages}</span>
                    <button 
                      disabled={creatorPage >= creators.pagination.pages}
                      onClick={() => setCreatorPage(p => p + 1)}
                    >Next</button>
                  </div>
                )}
              </div>
            )}

            {/* BRANDS LIST SECTION */}
            {activeSection === 'brands-list' && (
              <div className="list-section">
                <div className="list-controls">
                  <input
                    type="text"
                    placeholder="Search brands by company, contact, or email..."
                    value={brandSearchInput}
                    onChange={(e) => setBrandSearchInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        setBrandSearchApplied(brandSearchInput);
                        setBrandPage(1);
                      }
                    }}
                    className="search-input"
                  />
                  <button 
                    onClick={() => {
                      setBrandSearchApplied(brandSearchInput);
                      setBrandPage(1);
                    }} 
                    className="search-btn"
                  >
                    🔍 Search
                  </button>
                  {brandSearchApplied && (
                    <button 
                      onClick={() => {
                        setBrandSearchInput('');
                        setBrandSearchApplied('');
                        setBrandPage(1);
                      }} 
                      className="clear-btn"
                    >
                      ✕ Clear
                    </button>
                  )}
                  <button onClick={() => handleExport('brands')} className="export-btn-inline">📥 Export CSV</button>
                </div>
                {brandSearchApplied && (
                  <div className="search-info">
                    Showing results for: <strong>"{brandSearchApplied}"</strong>
                  </div>
                )}

                <div className="data-table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Company</th>
                        <th>Contact</th>
                        <th>Email</th>
                        <th>Industry</th>
                        <th>Campaigns</th>
                        <th>Total Spend</th>
                        <th>Joined</th>
                      </tr>
                    </thead>
                    <tbody>
                      {brands.list.map(b => (
                        <tr key={b.id}>
                          <td className="name-cell">{b.company_name}</td>
                          <td>{b.contact_name || '-'}</td>
                          <td className="email-cell">{b.email}</td>
                          <td>{b.industry || '-'}</td>
                          <td>
                            <span className="campaign-count">{b.total_campaigns} ({b.active_campaigns} active)</span>
                          </td>
                          <td className="spend-cell">₹{b.total_spend?.toLocaleString() || 0}</td>
                          <td>{formatDate(b.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {brands.pagination && (
                  <div className="pagination">
                    <button 
                      disabled={brandPage <= 1} 
                      onClick={() => setBrandPage(p => p - 1)}
                    >Previous</button>
                    <span>Page {brandPage} of {brands.pagination.pages}</span>
                    <button 
                      disabled={brandPage >= brands.pagination.pages}
                      onClick={() => setBrandPage(p => p + 1)}
                    >Next</button>
                  </div>
                )}
              </div>
            )}

            {/* CAMPAIGNS SECTION */}
            {activeSection === 'campaigns' && campaignAnalytics && (
              <div className="campaigns-section">
                <div className="stats-grid small">
                  <div className="stat-card">
                    <span className="stat-value">{campaignAnalytics.total}</span>
                    <span className="stat-label">Total Campaigns</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-value">₹{campaignAnalytics.budget_analysis?.total?.toLocaleString() || 0}</span>
                    <span className="stat-label">Total Budget</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-value">₹{campaignAnalytics.budget_analysis?.average?.toLocaleString() || 0}</span>
                    <span className="stat-label">Avg Budget</span>
                  </div>
                </div>

                <div className="section-row">
                  <div className="section-card">
                    <h3>📊 Status Breakdown</h3>
                    <div className="status-breakdown">
                      {campaignAnalytics.status_breakdown?.map((s, i) => (
                        <div key={i} className="status-item">
                          <span className={`status-badge ${s.status}`}>{s.status}</span>
                          <span className="status-count">{s.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="section-card">
                    <h3>🎨 Top Niches</h3>
                    <div className="distribution">
                      {campaignAnalytics.niche_distribution?.slice(0, 5).map((n, i) => (
                        <div key={i} className="dist-item">
                          <span>{n.niche}</span>
                          <span className="dist-count">{n.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="section-card full-width">
                  <h3>📋 All Campaigns</h3>
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Campaign</th>
                          <th>Brand</th>
                          <th>Status</th>
                          <th title="Funds actually blocked in escrow for this campaign">Funds Blocked</th>
                          <th>Creators</th>
                          <th>Niche</th>
                          <th>Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaignAnalytics.campaigns?.map(c => (
                          <tr key={c.id || c.campaign_id || c._id}>
                            <td className="name-cell">{c.name || c.campaign_name || 'Unnamed'}</td>
                            <td>{c.brand || c.brand_name || '-'}</td>
                            <td><span className={`status-badge ${c.status}`}>{c.status}</span></td>
                            <td>₹{(c.funds_blocked ?? c.escrow_amount ?? c.budget ?? c.budget_max ?? 0).toLocaleString()}</td>
                            <td>{c.required_creators || c.creator_count_expected || '-'}</td>
                            <td>{c.niche || '-'}</td>
                            <td>{formatDate(c.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* INVITATIONS SECTION */}
            {activeSection === 'invitations' && invitationAnalytics && (
              <div className="invitations-section">
                <div className="stats-grid small">
                  <div className="stat-card">
                    <span className="stat-value">{invitationAnalytics.summary?.total || 0}</span>
                    <span className="stat-label">Total Sent</span>
                  </div>
                  <div className="stat-card success">
                    <span className="stat-value">{invitationAnalytics.summary?.accepted || 0}</span>
                    <span className="stat-label">Accepted</span>
                  </div>
                  <div className="stat-card danger">
                    <span className="stat-value">{invitationAnalytics.summary?.rejected || 0}</span>
                    <span className="stat-label">Rejected</span>
                  </div>
                  <div className="stat-card warning">
                    <span className="stat-value">{invitationAnalytics.summary?.pending || 0}</span>
                    <span className="stat-label">Pending</span>
                  </div>
                  <div className="stat-card muted">
                    <span className="stat-value">{invitationAnalytics.summary?.expired || 0}</span>
                    <span className="stat-label">Expired</span>
                  </div>
                  <div className="stat-card info">
                    <span className="stat-value">
                      {(invitationAnalytics.summary?.responded ?? 0) > 0
                        ? `${invitationAnalytics.summary?.acceptance_rate ?? 0}%`
                        : '—'}
                    </span>
                    <span className="stat-label">Accept Rate</span>
                    <span className="stat-sub" style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      of {invitationAnalytics.summary?.responded ?? 0} responded
                    </span>
                  </div>
                </div>

                <div className="section-card full-width">
                  <h3>❌ Rejected Invitations (for follow-up)</h3>
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Creator</th>
                          <th>Email</th>
                          <th>Instagram</th>
                          <th>Campaign</th>
                          <th>Offered Price</th>
                          <th>Rejected At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invitationAnalytics.rejected_invitations?.map((inv, i) => (
                          <tr key={i}>
                            <td>{inv.creator?.name}</td>
                            <td className="email-cell">{inv.creator?.email}</td>
                            <td>@{inv.creator?.username}</td>
                            <td>{inv.campaign?.name}</td>
                            <td>₹{inv.allocated_price?.toLocaleString() || 0}</td>
                            <td>{formatDate(inv.rejected_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* PAYMENTS SECTION */}
            {activeSection === 'payments' && (
              <div className="payments-section">
                <h2>💰 Payment Management</h2>

                {/* Primary Filter: Brands vs Creators */}
                <div className="payment-type-tabs">
                  <button 
                    className={`type-tab ${paymentTypeFilter === 'brands' ? 'active' : ''}`}
                    onClick={() => { setPaymentTypeFilter('brands'); setPaymentStatusFilter('pending'); }}
                  >
                    🏢 Brand Payments (Escrow)
                  </button>
                  <button 
                    className={`type-tab ${paymentTypeFilter === 'creators' ? 'active' : ''}`}
                    onClick={() => { setPaymentTypeFilter('creators'); setPaymentStatusFilter('pending'); }}
                  >
                    👤 Creator Payouts
                  </button>
                </div>

                {/* Secondary Filter: Status */}
                <div className="payment-status-tabs">
                  <button 
                    className={`status-tab ${paymentStatusFilter === 'pending' ? 'active' : ''}`}
                    onClick={() => setPaymentStatusFilter('pending')}
                  >
                    ⏳ Pending
                  </button>
                  <button 
                    className={`status-tab ${paymentStatusFilter === 'completed' ? 'active' : ''}`}
                    onClick={() => setPaymentStatusFilter('completed')}
                  >
                    ✅ Completed
                  </button>
                  <button 
                    className={`status-tab ${paymentStatusFilter === 'failed' ? 'active' : ''}`}
                    onClick={() => setPaymentStatusFilter('failed')}
                  >
                    ❌ Failed
                  </button>
                </div>

                {/* BRANDS SECTION */}
                {paymentTypeFilter === 'brands' && (
                  <div className="brands-payments">
                    {/* Summary for Brands */}
                    <div className="stats-grid small">
                      <div className="stat-card warning">
                        <span className="stat-value">{pendingRefunds.summary?.count_pending || 0}</span>
                        <span className="stat-label">Pending Refunds</span>
                      </div>
                      <div className="stat-card success">
                        <span className="stat-value">₹{(pendingRefunds.summary?.total_completed || 0).toLocaleString()}</span>
                        <span className="stat-label">Refunded</span>
                      </div>
                      <div className="stat-card danger">
                        <span className="stat-value">{pendingRefunds.summary?.count_failed || 0}</span>
                        <span className="stat-label">Failed</span>
                      </div>
                    </div>

                    <div className="section-card full-width">
                      <h3>
                        {paymentStatusFilter === 'pending' && '⏳ Pending Brand Refunds'}
                        {paymentStatusFilter === 'completed' && '✅ Completed Brand Refunds'}
                        {paymentStatusFilter === 'failed' && '❌ Failed Brand Refunds'}
                      </h3>

                      {(() => {
                        let filteredBrandPayments = [];
                        if (paymentStatusFilter === 'pending') {
                          filteredBrandPayments = (pendingRefunds.refunds || []).filter(r => ['pending', 'processing'].includes(r.status));
                        } else if (paymentStatusFilter === 'completed') {
                          filteredBrandPayments = pendingRefunds.completed_refunds || [];
                        } else if (paymentStatusFilter === 'failed') {
                          filteredBrandPayments = (pendingRefunds.refunds || []).filter(r => r.status === 'failed');
                        }

                        if (filteredBrandPayments.length === 0) {
                          return (
                            <div className="empty-state">
                              <span>{paymentStatusFilter === 'pending' ? '✅' : '📭'}</span>
                              <p>No {paymentStatusFilter} brand payments.</p>
                            </div>
                          );
                        }

                        return (
                          <div className="data-table-container">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Campaign</th>
                                  <th>Brand</th>
                                  <th>Escrow</th>
                                  <th>Platform Fee</th>
                                  <th>Refund Amount</th>
                                  <th>Status</th>
                                  {paymentStatusFilter !== 'completed' && <th>Action</th>}
                                </tr>
                              </thead>
                              <tbody>
                                {filteredBrandPayments.map(r => (
                                  <tr key={r.refund_id}>
                                    <td>{r.campaign_name}</td>
                                    <td>
                                      <div>{r.brand_name}</div>
                                      <small style={{ color: '#6b7280' }}>{r.brand_email}</small>
                                    </td>
                                    <td>₹{(r.escrow_amount || 0).toLocaleString()}</td>
                                    <td className="negative">-₹{(r.platform_fee || 0).toLocaleString()}</td>
                                    <td className="amount-cell">₹{(r.net_refund || r.amount || 0).toLocaleString()}</td>
                                    <td>
                                      <span className={`status-badge ${r.status}`}>
                                        {r.status === 'pending' ? '⏳' : r.status === 'processing' ? '🔄' : r.status === 'completed' ? '✅' : '❌'} {r.status}
                                      </span>
                                    </td>
                                    {paymentStatusFilter !== 'completed' && (
                                      <td>
                                        <button 
                                          className="complete-btn small"
                                          onClick={async () => {
                                            const utr = prompt('Enter UTR/Reference (leave empty for Razorpay):');
                                            if (window.confirm(`Mark refund of ₹${(r.net_refund || r.amount)?.toLocaleString()} as completed?`)) {
                                              try {
                                                await processAdminRefund({ refund_id: r.refund_id, action: 'complete', utr_reference: utr });
                                                fetchData();
                                              } catch (err) {
                                                alert('Failed: ' + (err.response?.data?.detail || err.message));
                                              }
                                            }
                                          }}
                                        >
                                          ✅ Complete
                                        </button>
                                      </td>
                                    )}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* CREATORS SECTION */}
                {paymentTypeFilter === 'creators' && (
                  <div className="creators-payments">
                    {/* Summary for Creators */}
                    <div className="stats-grid small">
                      <div className="stat-card warning">
                        <span className="stat-value">{pendingPayments.summary?.campaigns_with_pending || 0}</span>
                        <span className="stat-label">Campaigns Pending</span>
                      </div>
                      <div className="stat-card danger">
                        <span className="stat-value">₹{(pendingPayments.summary?.total_pending_amount || 0).toLocaleString()}</span>
                        <span className="stat-label">Pending Amount</span>
                      </div>
                      <div className="stat-card success">
                        <span className="stat-value">₹{(paymentHistory.totalAmount || 0).toLocaleString()}</span>
                        <span className="stat-label">Total Paid</span>
                      </div>
                    </div>

                    <div className="section-card full-width">
                      <h3>
                        {paymentStatusFilter === 'pending' && '⏳ Pending Creator Payouts'}
                        {paymentStatusFilter === 'completed' && '✅ Completed Creator Payouts'}
                        {paymentStatusFilter === 'failed' && '❌ Failed Creator Payouts'}
                      </h3>

                      {/* Pending Creator Payments */}
                      {paymentStatusFilter === 'pending' && (
                        <>
                          {pendingPayments.campaigns?.length === 0 ? (
                            <div className="empty-state">
                              <span>✅</span>
                              <p>No pending creator payments!</p>
                            </div>
                          ) : (
                            pendingPayments.campaigns?.map(campaign => (
                              <div key={campaign.campaign_id} className="payment-campaign-card">
                                <div className="payment-campaign-header">
                                  <div>
                                    <h4>{campaign.campaign_name}</h4>
                                    <span className="brand-name">by {campaign.brand_name}</span>
                                  </div>
                                  <div className="campaign-amounts">
                                    <span className="pending">₹{campaign.total_pending?.toLocaleString()} pending</span>
                                    <span className="paid">₹{campaign.total_paid?.toLocaleString()} paid</span>
                                  </div>
                                </div>
                                <div className="payment-creators-list">
                                  {campaign.creators?.map(creator => {
                                    const awaitingReconnect = creator.awaiting_creator_reconnect;
                                    const installments = creator.installments || [];
                                    const matured = installments.filter(i => i.is_matured || i.payment_status === 'paid');
                                    const allInstallmentsPaid = installments.length > 0
                                      && installments.every(i => i.payment_status === 'paid');

                                    if (matured.length === 0) return null;

                                    const paymentDetailsLabel = creator.payment_details
                                      ? (creator.payment_details.payment_method === 'upi'
                                        ? <>UPI: {creator.payment_details.upi_id}</>
                                        : <>Bank: {creator.payment_details.account_number || '—'}</>)
                                      : <span className="no-details">No payment details</span>;

                                    return (
                                      <div key={creator.creator_id} className="payment-creator-block">
                                        <div className="payment-creator-header">
                                          <div className="creator-info">
                                            <span className="creator-name">{creator.name}</span>
                                            <span className="creator-email">{creator.email}</span>
                                          </div>
                                          <div className="creator-meta">
                                            <span className="creator-payment-method">{paymentDetailsLabel}</span>
                                            {awaitingReconnect && (
                                              <span
                                                className="chip chip-warning"
                                                title="Creator's Instagram session expired during monitoring. Ask them to reconnect before paying the performance bonus so we release on up-to-date metrics."
                                              >⚠ Awaiting IG reconnect</span>
                                            )}
                                            {allInstallmentsPaid && (
                                              <span className="chip chip-muted">All installments paid</span>
                                            )}
                                          </div>
                                        </div>

                                        {installments.map(inst => {
                                          // Render every installment (matured + upcoming) so admin
                                          // can see the full timeline. Pay button is only enabled
                                          // when can_pay=true.
                                          const isPaid = inst.payment_status === 'paid';
                                          const status = inst.status || (isPaid ? 'paid' : (inst.is_matured ? 'ready' : 'upcoming'));
                                          const statusLabel = inst.status_label || (isPaid ? 'Paid' : (inst.is_matured ? 'Ready to pay' : 'Upcoming'));
                                          const canPay = inst.can_pay;

                                          const chipClass =
                                            status === 'paid' ? 'chip-muted'
                                            : status === 'ready' ? 'chip-success'
                                            : status === 'blocked' ? 'chip-warning'
                                            : status === 'current' ? 'chip-info'
                                            : 'chip-muted'; // upcoming

                                          const tooltip = isPaid
                                            ? 'Already paid'
                                            : status === 'current'
                                              ? 'This week is still in monitoring — payment opens once the week ends'
                                              : status === 'upcoming'
                                                ? statusLabel
                                                : inst.blocked_reason === 'awaiting_ig_reconnect'
                                                  ? 'Blocked: creator must reconnect Instagram so we release on fresh metrics'
                                                  : inst.blocked_reason === 'no_payment_details'
                                                    ? 'Blocked: creator has not added payment details'
                                                    : '';

                                          // Performance bonus amount is unknown until the
                                          // scheduler computes final_metrics. Don't show a
                                          // misleading "₹X" upper bound — just hyphen out the
                                          // amount cell until it's real.
                                          const isPerfPending = inst.type === 'performance_bonus' && status === 'upcoming';

                                          return (
                                            <div key={inst.id} className={`payment-installment-row payment-installment-row--${status}`}>
                                              <div className="installment-label">
                                                <span className="installment-name">{inst.label}</span>
                                                {!isPerfPending && (
                                                  <span className="installment-sub">
                                                    Gross ₹{inst.gross?.toLocaleString()} · Fee ₹{inst.platform_fee?.toLocaleString()}
                                                  </span>
                                                )}
                                              </div>
                                              <div className="installment-chip">
                                                <span className={`chip ${chipClass}`} title={statusLabel}>
                                                  {statusLabel}
                                                </span>
                                              </div>
                                              <div className="installment-amount">
                                                {isPerfPending ? '—' : `₹${inst.net_payable?.toLocaleString()}`}
                                              </div>
                                              <div className="installment-action">
                                                <button
                                                  className="pay-btn"
                                                  onClick={() => handlePayCreator(campaign, creator, inst)}
                                                  disabled={!canPay || isPaid}
                                                  title={tooltip}
                                                >
                                                  {isPaid ? '✓ Paid' : '💳 Pay'}
                                                </button>
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ))
                          )}
                        </>
                      )}

                      {/* Completed Creator Payments */}
                      {paymentStatusFilter === 'completed' && (
                        <>
                          {paymentHistory.transactions?.length === 0 ? (
                            <div className="empty-state">
                              <span>📭</span>
                              <p>No completed payments yet.</p>
                            </div>
                          ) : (
                            <div className="data-table-container">
                              <table className="data-table">
                                <thead>
                                  <tr>
                                    <th>Date</th>
                                    <th>Creator</th>
                                    <th>Campaign</th>
                                    <th>Amount</th>
                                    <th>Method</th>
                                    <th>UTR</th>
                                    <th>Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {paymentHistory.transactions?.map(txn => (
                                    <tr key={txn.transaction_id}>
                                      <td>{formatDateTime(txn.created_at)}</td>
                                      <td>{txn.creator_name}</td>
                                      <td>{txn.campaign_name}</td>
                                      <td className="amount-cell">₹{txn.amount?.toLocaleString()}</td>
                                      <td>{txn.payment_method?.toUpperCase()}</td>
                                      <td className="utr-cell">{txn.utr_reference}</td>
                                      <td><span className="status-badge completed">✅</span></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </>
                      )}

                      {/* Failed Creator Payments */}
                      {paymentStatusFilter === 'failed' && (
                        <div className="empty-state">
                          <span>✅</span>
                          <p>No failed creator payments.</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* COMMUNICATIONS SECTION */}
            {activeSection === 'communications' && (
              <div className="communications-section">
                <div className="section-card full-width">
                  <h3>📞 Creators Who Rejected Invitations (Need Follow-up)</h3>
                  <p className="section-description">These creators rejected campaign invitations. You may want to reach out to understand their concerns.</p>
                  
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Creator</th>
                          <th>Email</th>
                          <th>Phone</th>
                          <th>Instagram</th>
                          <th>Rejections</th>
                          <th>Last Rejection</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rejectedCreators?.map(c => (
                          <tr key={c.creator_id}>
                            <td className="name-cell">{c.name}</td>
                            <td className="email-cell">
                              <a href={`mailto:${c.email}`}>{c.email}</a>
                            </td>
                            <td>{c.phone || '-'}</td>
                            <td>@{c.instagram_username}</td>
                            <td><span className="rejection-count">{c.rejection_count}</span></td>
                            <td>{formatDate(c.last_rejection)}</td>
                            <td>
                              <a href={`mailto:${c.email}?subject=Milan AI - Let's Connect!`} className="action-btn">
                                ✉️ Email
                              </a>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* REVENUE DASHBOARD SECTION */}
            {activeSection === 'revenue' && revenueData && (
              <div className="revenue-section">
                {/* Revenue Stats */}
                <div className="stats-grid">
                  <div className="stat-card primary large">
                    <div className="stat-icon">💵</div>
                    <div className="stat-info">
                      <span className="stat-value">₹{revenueData.revenue.total_platform_fees?.toLocaleString() || 0}</span>
                      <span className="stat-label">Total Platform Revenue</span>
                      <span className="stat-sub">
                        Escrow ₹{(typeof revenueData.revenue.total_escrow_platform_fees === 'number'
                          ? revenueData.revenue.total_escrow_platform_fees
                          : (revenueData.revenue.total_platform_fees ?? 0)).toLocaleString()} ·
                        On release ₹{(revenueData.revenue.total_release_platform_fees ?? 0).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <div className="stat-card success large">
                    <div className="stat-icon">🏦</div>
                    <div className="stat-info">
                      <span className="stat-value">₹{revenueData.revenue.total_escrow_collected?.toLocaleString() || 0}</span>
                      <span className="stat-label">Total Escrow Collected</span>
                      <span className="stat-sub">{revenueData.revenue.campaigns_count} campaigns</span>
                    </div>
                  </div>
                  <div className="stat-card warning large">
                    <div className="stat-icon">💳</div>
                    <div className="stat-info">
                      <span className="stat-value">₹{revenueData.revenue.total_paid_to_creators?.toLocaleString() || 0}</span>
                      <span className="stat-label">Paid to Creators</span>
                      <span className="stat-sub">{revenueData.revenue.payments_count} payments</span>
                    </div>
                  </div>
                  <div className="stat-card info large">
                    <div className="stat-icon">⏳</div>
                    <div className="stat-info">
                      <span className="stat-value">₹{revenueData.revenue.pending_escrow?.toLocaleString() || 0}</span>
                      <span className="stat-label">Pending Escrow</span>
                      <span className="stat-sub">₹{revenueData.revenue.pending_fees?.toLocaleString() || 0} fees</span>
                    </div>
                  </div>
                </div>

                <div className="section-card full-width">
                  <h3>↩️ Brand refunds</h3>
                  <div className="period-stats">
                    <div>
                      <span className="num">₹{(revenueData.revenue.refunds_to_brands_total ?? 0).toLocaleString()}</span>
                      {' '}Refunded to brands
                    </div>
                    <div>
                      <span className="num">{revenueData.revenue.refunds_count ?? 0}</span>
                      {' '}Completed refunds
                    </div>
                    <div>
                      <span className="num">₹{(revenueData.revenue.refunds_platform_fee_retained ?? 0).toLocaleString()}</span>
                      {' '}Platform fee retained
                    </div>
                  </div>
                </div>

                {/* Monthly Breakdown */}
                {revenueData.monthly_breakdown?.length > 0 && (
                  <div className="section-card full-width">
                    <h3>📆 Monthly Revenue Breakdown</h3>
                    <div className="revenue-table">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Month</th>
                            <th>Escrow fees</th>
                            <th>Release fees</th>
                            <th>Fees (combined)</th>
                            <th>Total Escrow</th>
                            <th>Brand refunds</th>
                            <th># Refunds</th>
                            <th>Campaigns</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revenueData.monthly_breakdown.map(m => (
                            <tr key={m.month}>
                              <td>{m.month}</td>
                              <td className="amount-cell">₹{m.fees?.toLocaleString()}</td>
                              <td className="amount-cell">₹{(m.release_fees ?? 0).toLocaleString()}</td>
                              <td className="amount-cell">₹{(m.fees_combined ?? (m.fees || 0) + (m.release_fees || 0)).toLocaleString()}</td>
                              <td className="amount-cell">₹{m.escrow?.toLocaleString()}</td>
                              <td className="amount-cell">₹{(m.brand_refunds ?? 0).toLocaleString()}</td>
                              <td>{m.refund_count ?? 0}</td>
                              <td>{m.campaigns}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Top Paying Brands */}
                {revenueData.top_brands?.length > 0 && (
                  <div className="section-card full-width">
                    <h3>🏆 Top Paying Brands</h3>
                    <div className="top-brands-list">
                      {revenueData.top_brands.map((brand, index) => (
                        <div key={brand.brand_id} className="top-brand-item">
                          <span className="rank">#{index + 1}</span>
                          <span className="brand-name">{brand.name}</span>
                          <span className="campaigns">{brand.campaigns} campaigns</span>
                          <span className="spend">₹{brand.total_spend?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* CAMPAIGN ROI SECTION */}
            {activeSection === 'roi' && campaignROI && (
              <div className="roi-section">
                {/* Summary */}
                <div className="stats-grid">
                  <div className="stat-card primary">
                    <div className="stat-info">
                      <span className="stat-value">{campaignROI.summary.total_campaigns}</span>
                      <span className="stat-label">Campaigns Analyzed</span>
                    </div>
                  </div>
                  <div className="stat-card success">
                    <div className="stat-info">
                      <span className="stat-value">{campaignROI.summary.budget_utilization}%</span>
                      <span className="stat-label">Budget Utilization</span>
                    </div>
                  </div>
                  <div className="stat-card info">
                    <div className="stat-info">
                      <span className="stat-value">{campaignROI.summary.avg_acceptance_rate}%</span>
                      <span className="stat-label">Avg Acceptance Rate</span>
                    </div>
                  </div>
                </div>

                {/* Campaign ROI Table */}
                <div className="section-card full-width">
                  <h3>📈 Campaign Performance vs Expected</h3>
                  <div className="data-table-container">
                    <table className="data-table roi-table">
                      <thead>
                        <tr>
                          <th>Campaign</th>
                          <th>Brand</th>
                          <th>Status</th>
                          <th>Blocked escrow</th>
                          <th>Used (gross)</th>
                          <th>Creators (Exp/Act)</th>
                          <th>Acceptance Rate</th>
                          <th>Content</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaignROI.campaigns.map(c => (
                          <tr key={c.campaign_id}>
                            <td className="name-cell">{c.campaign_name}</td>
                            <td>{c.brand_name}</td>
                            <td><span className={`status-badge ${c.status}`}>{c.status}</span></td>
                            <td className="amount-cell">₹{c.expected.budget?.toLocaleString()}</td>
                            <td className="amount-cell">₹{c.actual.spend?.toLocaleString()}</td>
                            <td>{c.expected.creators} / {c.actual.creators}</td>
                            <td>
                              {(c.invitations.responded ?? 0) > 0 ? (
                                <span className={`rate ${c.invitations.acceptance_rate >= 50 ? 'good' : 'low'}`}>
                                  {c.invitations.acceptance_rate}%
                                </span>
                              ) : (
                                <span className="rate muted">—</span>
                              )}
                            </td>
                            <td>
                              <div className="content-stats">
                                <span className="approved">✓{c.content.approved}</span>
                                <span className="rejected">✗{c.content.rejected}</span>
                                <span className="pending">⏳{c.content.pending}</span>
                              </div>
                            </td>
                            <td>
                              <button 
                                className="view-btn"
                                onClick={() => openROIDetailModal(c)}
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* CONTENT REJECTIONS SECTION */}
            {activeSection === 'content-rejections' && (
              <div className="content-rejections-section">
                <div className="section-card full-width">
                  <h3>📝 All Content Rejections</h3>
                  <p className="section-description">Track all content submissions that were rejected by brands.</p>
                  
                  {contentRejections.length === 0 ? (
                    <div className="empty-state">
                      <span>✅</span>
                      <p>No rejected content in this period.</p>
                    </div>
                  ) : (
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Creator</th>
                            <th>Campaign</th>
                            <th>Content Type</th>
                            <th>Status</th>
                            <th>Rejection Reason</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {contentRejections.map(r => (
                            <tr key={r.review_id}>
                              <td>
                                <div className="creator-cell">
                                  <span>{r.creator.name}</span>
                                  <span className="sub">@{r.creator.username}</span>
                                </div>
                              </td>
                              <td>{r.campaign.name}</td>
                              <td>{r.content_type}</td>
                              <td><span className={`status-badge ${r.status}`}>{r.status}</span></td>
                              <td className="reason-cell">{r.rejection_reason || '-'}</td>
                              <td>{formatDateTime(r.reviewed_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* PLATFORM HEALTH SECTION */}
            {activeSection === 'platform-health' && (
              <div className="platform-health-section">
                {/* System Status */}
                {platformHealth && (
                  <div className="stats-grid">
                    <div className={`stat-card ${platformHealth.system.database === 'healthy' ? 'success' : 'danger'}`}>
                      <div className="stat-icon">🗄️</div>
                      <div className="stat-info">
                        <span className="stat-value">{platformHealth.system.database}</span>
                        <span className="stat-label">Database</span>
                      </div>
                    </div>
                    <div className={`stat-card ${platformHealth.system.api === 'healthy' ? 'success' : 'danger'}`}>
                      <div className="stat-icon">⚡</div>
                      <div className="stat-info">
                        <span className="stat-value">{platformHealth.system.api}</span>
                        <span className="stat-label">API</span>
                      </div>
                    </div>
                    <div className="stat-card warning">
                      <div className="stat-icon">⏳</div>
                      <div className="stat-info">
                        <span className="stat-value">{platformHealth.operations.pending_invitations}</span>
                        <span className="stat-label">Pending Invitations</span>
                      </div>
                    </div>
                    <div className="stat-card info">
                      <div className="stat-icon">💳</div>
                      <div className="stat-info">
                        <span className="stat-value">{platformHealth.operations.pending_payments}</span>
                        <span className="stat-label">Pending Payments</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Operations */}
                {platformHealth && (
                  <div className="section-row">
                    <div className="section-card">
                      <h3>⚙️ Operations Status</h3>
                      <div className="operations-list">
                        <div className="operation-item">
                          <span>Active Campaigns</span>
                          <span className="value">{platformHealth.operations.active_campaigns}</span>
                        </div>
                        <div className="operation-item">
                          <span>Stalled Campaigns</span>
                          <span className="value warning">{platformHealth.operations.stalled_campaigns}</span>
                        </div>
                        <div className="operation-item">
                          <span>Failed Payments (24h)</span>
                          <span className="value danger">{platformHealth.operations.failed_payments_24h}</span>
                        </div>
                      </div>
                    </div>
                    <div className="section-card">
                      <h3>👥 User Activity (24h)</h3>
                      <div className="activity-list">
                        <div className="activity-item">
                          <span>Active Creators</span>
                          <span className="value">{platformHealth.activity.active_creators_24h}</span>
                        </div>
                        <div className="activity-item">
                          <span>Active Brands</span>
                          <span className="value">{platformHealth.activity.active_brands_24h}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Alerts */}
                <div className="section-card full-width alerts-section">
                  <h3>🔔 Active Alerts ({alerts.summary?.total || 0})</h3>
                  {alerts.alerts?.length === 0 ? (
                    <div className="empty-state">
                      <span>✅</span>
                      <p>No active alerts!</p>
                    </div>
                  ) : (
                    <div className="alerts-list">
                      {alerts.alerts?.map((alert, index) => (
                        <div key={index} className={`alert-item ${alert.severity}`}>
                          <div className="alert-header">
                            <span className={`severity-badge ${alert.severity}`}>{alert.severity.toUpperCase()}</span>
                            <span className="alert-type">{alert.type.replace(/_/g, ' ')}</span>
                          </div>
                          <div className="alert-content">
                            <h4>{alert.title}</h4>
                            <p>{alert.message}</p>
                          </div>
                          <div className="alert-actions">
                            <button onClick={() => setActiveSection(alert.action_url.replace('/admin?section=', ''))}>
                              View Details
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* BULK EMAIL SECTION */}
            {activeSection === 'bulk-email' && (
              <div className="bulk-email-section">
                <div className="section-row">
                  <div className="section-card">
                    <h3>📨 Send Bulk Email</h3>
                    <form onSubmit={handleSendBulkEmail} className="bulk-email-form">
                      <div className="form-group">
                        <label>Recipients</label>
                        <select
                          value={bulkEmailForm.recipient_type}
                          onChange={(e) => setBulkEmailForm(prev => ({ ...prev, recipient_type: e.target.value }))}
                        >
                          <option value="creators">All Creators</option>
                          <option value="brands">All Brands</option>
                          <option value="all">Everyone</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Subject *</label>
                        <input
                          type="text"
                          placeholder="Email subject..."
                          value={bulkEmailForm.subject}
                          onChange={(e) => setBulkEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="form-group">
                        <label>Message *</label>
                        <textarea
                          placeholder="Your message here..."
                          value={bulkEmailForm.message}
                          onChange={(e) => setBulkEmailForm(prev => ({ ...prev, message: e.target.value }))}
                          rows={6}
                          required
                        />
                      </div>
                      <button type="submit" className="send-btn" disabled={sendingEmail}>
                        {sendingEmail ? 'Sending...' : '📤 Send Email'}
                      </button>
                    </form>
                  </div>

                  <div className="section-card">
                    <h3>📜 Email History</h3>
                    <div className="email-history">
                      {bulkEmailHistory.length === 0 ? (
                        <p className="no-data">No emails sent yet.</p>
                      ) : (
                        bulkEmailHistory.map(log => (
                          <div key={log.id} className="email-log-item">
                            <div className="log-header">
                              <span className="subject">{log.subject}</span>
                              <span className="date">{formatDateTime(log.created_at)}</span>
                            </div>
                            <div className="log-stats">
                              <span>To: {log.recipient_type}</span>
                              <span className="sent">✓ {log.sent_count} sent</span>
                              {log.failed_count > 0 && <span className="failed">✗ {log.failed_count} failed</span>}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* CAMPAIGN CONTROLS SECTION */}
            {activeSection === 'campaign-controls' && campaignAnalytics && (
              <div className="campaign-controls-section">
                <div className="section-card full-width">
                  <h3>⚙️ Campaign Management</h3>
                  <p className="section-description">Force complete, pause, or cancel campaigns.</p>
                  
                  <div className="data-table-container">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>Campaign</th>
                          <th>Brand</th>
                          <th>Status</th>
                          <th title="Funds actually blocked in escrow for this campaign">Funds Blocked</th>
                          <th>Created</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {campaignAnalytics.campaigns?.map(c => (
                          <tr key={c.campaign_id || c.id || c._id}>
                            <td className="name-cell">{c.name || c.campaign_name || 'Unnamed'}</td>
                            <td>{c.brand_name || c.brand || '-'}</td>
                            <td><span className={`status-badge ${c.status}`}>{c.status}</span></td>
                            <td className="amount-cell">₹{(c.funds_blocked ?? c.escrow_amount ?? c.budget ?? c.budget_max ?? 0).toLocaleString()}</td>
                            <td>{formatDate(c.created_at)}</td>
                            <td>
                              <button 
                                className="control-btn"
                                onClick={() => openCampaignControlModal({...c, campaign_id: c.campaign_id || c.id || c._id})}
                              >
                                ⚙️ Actions
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* REFUNDS SECTION */}
            {activeSection === 'refunds' && (
              <div className="refunds-section">
                <div className="section-header-row">
                  <h3>↩️ Refund Management</h3>
                  <button className="primary-btn" onClick={() => setShowRefundModal(true)}>
                    + Initiate Refund
                  </button>
                </div>

                {/* Razorpay Balance Banner — advisory; refunds will fail if
                    balance < amount due. Click Refresh to re-check. */}
                {(() => {
                  const bal = razorpayBalance;
                  const fetched = bal?.fetched;
                  const available = fetched ? bal?.available_rupees : null;
                  // Find the largest pending/failed refund amount so we can
                  // warn proactively when the merchant balance is below it.
                  const allActive = (pendingRefunds.refunds || []).filter(
                    r => ['pending', 'processing', 'failed', 'manual_pending'].includes(r.status)
                  );
                  const maxRefund = allActive.reduce(
                    (m, r) => Math.max(m, Number(r.net_refund || r.amount || 0)),
                    0
                  );
                  const insufficient =
                    fetched && available !== null && maxRefund > 0 && available < maxRefund;

                  const baseStyle = {
                    padding: '12px 16px',
                    borderRadius: 8,
                    marginBottom: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    fontSize: 14,
                    fontWeight: 500,
                  };
                  let bgColor, fgColor, icon, message;
                  if (balanceLoading && !bal) {
                    bgColor = '#f3f4f6';
                    fgColor = '#374151';
                    icon = '⏳';
                    message = 'Checking Razorpay balance…';
                  } else if (!fetched) {
                    bgColor = '#fef3c7';
                    fgColor = '#92400e';
                    icon = '⚠️';
                    message =
                      bal?.hint ||
                      'Could not fetch live Razorpay balance. Retry will still attempt the refund — Razorpay will reject if funds are insufficient.';
                  } else if (insufficient) {
                    bgColor = '#fee2e2';
                    fgColor = '#991b1b';
                    icon = '🚨';
                    message = `Razorpay balance ₹${(available || 0).toLocaleString()} is BELOW the largest open refund (₹${maxRefund.toLocaleString()}). Top up at https://dashboard.razorpay.com or use 'Mark Complete' after a manual bank transfer.`;
                  } else {
                    bgColor = '#dcfce7';
                    fgColor = '#166534';
                    icon = '✅';
                    message = `Razorpay merchant balance: ₹${(available || 0).toLocaleString()} available for refunds.`;
                  }
                  return (
                    <div style={{ ...baseStyle, background: bgColor, color: fgColor }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: 18 }}>{icon}</span>
                        <span>{message}</span>
                      </span>
                      <button
                        onClick={fetchRazorpayBalance}
                        disabled={balanceLoading}
                        style={{
                          background: 'transparent',
                          border: `1px solid ${fgColor}`,
                          color: fgColor,
                          padding: '4px 12px',
                          borderRadius: 6,
                          cursor: balanceLoading ? 'wait' : 'pointer',
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {balanceLoading ? '…' : '🔄 Refresh'}
                      </button>
                    </div>
                  );
                })()}

                {/* Refund Summary Cards */}
                {pendingRefunds.summary && (
                  <div className="refund-summary-cards" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
                    <div className="summary-card pending">
                      <span className="count">{(pendingRefunds.summary.count_pending || 0) + (pendingRefunds.summary.count_processing || 0) + (pendingRefunds.summary.count_manual_pending || 0)}</span>
                      <span className="label">In Progress</span>
                      <span className="amount">₹{((pendingRefunds.summary.total_pending || 0) + (pendingRefunds.summary.total_processing || 0) + (pendingRefunds.summary.total_manual_pending || 0)).toLocaleString()}</span>
                    </div>
                    <div className="summary-card" style={{ borderLeftColor: '#a855f7' }} title="Refund forecasted (e.g. backup creator activated). Becomes processable when the campaign completes.">
                      <span className="count">{pendingRefunds.summary.count_awaiting || 0}</span>
                      <span className="label">Awaiting Completion</span>
                      <span className="amount" style={{ color: '#a855f7' }}>₹{(pendingRefunds.summary.total_awaiting || 0).toLocaleString()}</span>
                    </div>
                    <div className="summary-card failed">
                      <span className="count">{pendingRefunds.summary.count_failed || 0}</span>
                      <span className="label">Failed</span>
                    </div>
                    <div className="summary-card" style={{ borderLeftColor: '#10b981' }}>
                      <span className="count">{pendingRefunds.summary.count_completed || 0}</span>
                      <span className="label">Completed</span>
                      <span className="amount" style={{ color: '#10b981' }}>₹{(pendingRefunds.summary.total_completed || 0).toLocaleString()}</span>
                    </div>
                    <div className="summary-card" style={{ borderLeftColor: '#6366f1' }}>
                      <span className="count">{(pendingRefunds.refunds?.length || 0) + (pendingRefunds.completed_refunds?.length || 0)}</span>
                      <span className="label">Total Refunds</span>
                    </div>
                  </div>
                )}

                {/* Filter Tabs */}
                <div className="refund-filter-tabs admin-refund-tabs">
                  <button 
                    className={`filter-tab ${refundFilter === 'in_progress' ? 'active' : ''}`}
                    onClick={() => setRefundFilter('in_progress')}
                  >
                    🔄 In Progress ({pendingRefunds.refunds?.filter(r => ['pending', 'processing', 'manual_pending'].includes(r.status)).length || 0})
                  </button>
                  <button
                    className={`filter-tab ${refundFilter === 'awaiting' ? 'active' : ''}`}
                    onClick={() => setRefundFilter('awaiting')}
                    title="Refunds forecasted but not yet actionable (e.g. backup creator activated, awaiting campaign completion)"
                  >
                    🕒 Awaiting Completion ({pendingRefunds.refunds?.filter(r => r.status === 'awaiting_completion').length || 0})
                  </button>
                  <button 
                    className={`filter-tab ${refundFilter === 'failed' ? 'active' : ''}`}
                    onClick={() => setRefundFilter('failed')}
                  >
                    ❌ Failed ({pendingRefunds.refunds?.filter(r => r.status === 'failed').length || 0})
                  </button>
                  <button 
                    className={`filter-tab ${refundFilter === 'completed' ? 'active' : ''}`}
                    onClick={() => setRefundFilter('completed')}
                  >
                    ✅ Completed ({pendingRefunds.completed_refunds?.length || 0})
                  </button>
                </div>

                {/* Filtered Refunds List */}
                <div className="section-card full-width">
                  <h3>
                    {refundFilter === 'in_progress' && '🔄 In Progress Refunds'}
                    {refundFilter === 'awaiting' && '🕒 Awaiting Completion'}
                    {refundFilter === 'failed' && '❌ Failed Refunds'}
                    {refundFilter === 'completed' && '✅ Completed Refunds'}
                  </h3>

                  {/* In Progress, Awaiting & Failed Refunds */}
                  {(refundFilter === 'in_progress' || refundFilter === 'awaiting' || refundFilter === 'failed') && (
                    <>
                      {(() => {
                        const filteredRefunds = (pendingRefunds.refunds || []).filter(r => {
                          if (refundFilter === 'in_progress') return ['pending', 'processing', 'manual_pending'].includes(r.status);
                          if (refundFilter === 'awaiting') return r.status === 'awaiting_completion';
                          if (refundFilter === 'failed') return r.status === 'failed';
                          return false;
                        });
                        
                        if (filteredRefunds.length === 0) {
                          const emptyCopy = {
                            in_progress: { icon: '✅', text: 'No in-progress refunds.' },
                            awaiting: { icon: '🕒', text: 'No refunds waiting on campaign completion.' },
                            failed: { icon: '📭', text: 'No failed refunds.' },
                          }[refundFilter] || { icon: '📭', text: 'No refunds.' };
                          return (
                            <div className="empty-state">
                              <span>{emptyCopy.icon}</span>
                              <p>{emptyCopy.text}</p>
                            </div>
                          );
                        }

                        return (
                          <div className="refunds-list">
                            {filteredRefunds.map(r => (
                              <div key={r.refund_id} className={`refund-item status-${r.status}`}>
                                <div className="refund-header">
                                  <div className="refund-info">
                                    <h4>{r.campaign_name}</h4>
                                    <span className="brand">{r.brand_name}</span>
                                    <span className="email">{r.brand_email}</span>
                                  </div>
                                  <div className="refund-status">
                                    <span className={`status-badge ${r.status}`}>
                                      {r.status === 'pending' ? '⏳ Pending' :
                                       r.status === 'processing' ? '🔄 Processing' :
                                       r.status === 'failed' ? '❌ Failed' :
                                       r.status === 'manual_pending' ? '🏦 Manual Pending' :
                                       r.status === 'awaiting_completion' ? '🕒 Awaiting Completion' : r.status}
                                    </span>
                                    {r.type && r.type !== 'campaign_cancellation' && (
                                      <span
                                        className="status-badge"
                                        title="Refund category"
                                        style={{
                                          background: '#fdf4ff',
                                          color: '#86198f',
                                          fontSize: 11,
                                          padding: '2px 8px',
                                          borderRadius: 999,
                                          marginLeft: 4,
                                        }}
                                      >
                                        {r.type === 'creator_replacement_excess' ? 'Backup-replacement excess' :
                                         r.type === 'escrow_excess' ? 'Escrow excess' :
                                         r.type === 'admin_initiated' ? 'Admin initiated' :
                                         r.type === 'partial_fill_unfilled_slots' ? 'Unfilled slot (no backup left)' :
                                         r.type === 'auto_cancellation_no_creators' ? 'Auto-cancel (all rejected)' : r.type}
                                      </span>
                                    )}
                                    {/* Escrow + campaign badges expose state drift
                                        between the refund row and the underlying
                                        funds/lifecycle records. */}
                                    {r.escrow_status && (
                                      <span
                                        className="status-badge"
                                        title="Escrow document status"
                                        style={{
                                          background: '#eef2ff',
                                          color: '#3730a3',
                                          fontSize: 11,
                                          padding: '2px 8px',
                                          borderRadius: 999,
                                          marginLeft: 4,
                                        }}
                                      >
                                        Escrow: {r.escrow_status}
                                      </span>
                                    )}
                                    {r.campaign_payment_status && (
                                      <span
                                        className="status-badge"
                                        title="Campaign payment_status"
                                        style={{
                                          background: '#f0fdf4',
                                          color: '#166534',
                                          fontSize: 11,
                                          padding: '2px 8px',
                                          borderRadius: 999,
                                          marginLeft: 4,
                                        }}
                                      >
                                        Campaign: {r.campaign_payment_status}
                                      </span>
                                    )}
                                    {r.preflight_skipped && (
                                      <span
                                        title="Razorpay call was skipped because merchant balance was insufficient"
                                        style={{
                                          background: '#fef3c7',
                                          color: '#92400e',
                                          fontSize: 11,
                                          padding: '2px 8px',
                                          borderRadius: 999,
                                          marginLeft: 4,
                                        }}
                                      >
                                        ⚠️ Pre-flight skipped
                                      </span>
                                    )}
                                    {r.days_pending > 0 && (
                                      <span className="days-pending">{r.days_pending}d ago</span>
                                    )}
                                  </div>
                                </div>

                                <div className="refund-details">
                                  <div className="detail-row">
                                    <div className="detail-item">
                                      <span className="label">Escrow Amount</span>
                                      <span className="value">₹{(r.escrow_amount || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="detail-item">
                                      <span className="label">Platform Fee (Retained)</span>
                                      <span className="value negative">-₹{(r.platform_fee || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="detail-item">
                                      <span className="label">To Creators (Kill Fees)</span>
                                      <span className="value negative">-₹{(r.total_to_creators || 0).toLocaleString()}</span>
                                    </div>
                                    <div className="detail-item highlight">
                                      <span className="label">Net Refund to Brand</span>
                                      <span className="value">₹{(r.net_refund || r.amount || 0).toLocaleString()}</span>
                                    </div>
                                  </div>

                                  {r.creators_affected > 0 && (
                                    <div className="creators-affected">
                                      <span className="label">👥 {r.creators_affected} creator(s) affected</span>
                                      {r.creators_breakdown && r.creators_breakdown.length > 0 && (
                                        <div className="creators-breakdown">
                                          {r.creators_breakdown.slice(0, 3).map((c, i) => (
                                            <span key={i} className="creator-pill">
                                              {c.creator_name}: ₹{(c.creator_payment || 0).toLocaleString()}
                                            </span>
                                          ))}
                                          {r.creators_breakdown.length > 3 && (
                                            <span className="more">+{r.creators_breakdown.length - 3} more</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  {r.razorpay_refund_id && (
                                    <div className="razorpay-info">
                                      <span className="label">Razorpay ID:</span>
                                      <span className="value">{r.razorpay_refund_id}</span>
                                      <span className={`rp-status ${r.razorpay_status}`}>{r.razorpay_status}</span>
                                    </div>
                                  )}

                                  {r.razorpay_error && (
                                    <div className="error-info">
                                      <span className="label">⚠️ Error:</span>
                                      <span className="value">{r.razorpay_error}</span>
                                    </div>
                                  )}

                                  {r.reason && (
                                    <div className="reason-info">
                                      <span className="label">Reason:</span>
                                      <span className="value">{r.reason}</span>
                                    </div>
                                  )}

                                  {r.note && (
                                    <div className="note-info">
                                      <span className="label">Note:</span>
                                      <span className="value">{r.note}</span>
                                    </div>
                                  )}

                                  {/* Brand Bank Details for Refund */}
                                  {r.brand_bank_details ? (
                                    <div className="bank-details-info">
                                      <span className="label">🏦 Brand Bank Details:</span>
                                      <div className="bank-details-content">
                                        {r.brand_bank_details.payment_method === 'upi' ? (
                                          <span className="value">UPI: {r.brand_bank_details.upi_id}</span>
                                        ) : (
                                          <>
                                            <span className="value">{r.brand_bank_details.account_holder_name}</span>
                                            <span className="value">A/C: {r.brand_bank_details.account_number || '—'}</span>
                                            <span className="value">IFSC: {r.brand_bank_details.ifsc_code}</span>
                                            {r.brand_bank_details.bank_name && <span className="value">{r.brand_bank_details.bank_name}</span>}
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="bank-details-info missing">
                                      <span className="label">⚠️ No bank details on file</span>
                                      <span className="hint">Contact brand at {r.brand_email} for payment details</span>
                                    </div>
                                  )}
                                </div>

                                <div className="refund-actions">
                                  {r.status === 'awaiting_completion' && (
                                    <div
                                      style={{
                                        background: '#faf5ff',
                                        border: '1px solid #e9d5ff',
                                        color: '#6b21a8',
                                        padding: '10px 14px',
                                        borderRadius: 8,
                                        fontSize: 13,
                                        lineHeight: 1.5,
                                        flex: 1,
                                      }}
                                    >
                                      🕒 <strong>Forecast only.</strong> This refund will become processable when the campaign reaches{' '}
                                      <code>completed</code> state (all backup creator payouts done). It will then auto-flip to <strong>Pending</strong> and the Retry / Mark Complete buttons will appear here.
                                    </div>
                                  )}
                                  {(r.status === 'pending' || r.status === 'failed' || r.status === 'manual_pending') && (
                                    <button
                                      className="retry-btn"
                                      onClick={async () => {
                                        const refundAmt = Number(r.net_refund || r.amount || 0);
                                        // Pre-flight UI warning — backend also enforces this.
                                        const bal = razorpayBalance;
                                        if (bal?.fetched && bal?.available_rupees !== null && bal.available_rupees < refundAmt) {
                                          if (!window.confirm(
                                            `⚠️ Razorpay balance (₹${bal.available_rupees.toLocaleString()}) is BELOW refund amount (₹${refundAmt.toLocaleString()}).\n\n` +
                                            `The retry will be skipped server-side and the refund will stay in Failed.\n\n` +
                                            `Either top up your Razorpay account first, or use 'Mark Complete' after a manual bank transfer.\n\n` +
                                            `Continue anyway?`
                                          )) return;
                                        } else if (!window.confirm(
                                          `Retry Razorpay refund of ₹${refundAmt.toLocaleString()} for "${r.campaign_name}"?`
                                        )) return;

                                        try {
                                          const res = await processAdminRefund({ refund_id: r.refund_id, action: 'retry' });
                                          const outcome = res?.data?.outcome;
                                          const errMsg = res?.data?.error;
                                          if (outcome === 'success') {
                                            alert(`✅ Refund retry succeeded.\n\nRazorpay ID: ${res.data.razorpay_refund_id || 'pending'}`);
                                          } else if (outcome === 'failed') {
                                            alert(`❌ Refund retry failed.\n\n${errMsg || 'Razorpay rejected the request. See refund details for full error.'}`);
                                          } else if (outcome === 'manual') {
                                            alert(`⚠️ Refund routed to manual processing.\n\n${errMsg || 'No Razorpay payment ID — process via bank transfer and click Mark Complete.'}`);
                                          } else if (outcome === 'noop') {
                                            alert('Refund amount is 0. Marked as not required.');
                                          } else {
                                            alert(`Action processed (outcome: ${outcome || 'unknown'}).`);
                                          }
                                          fetchData();
                                          fetchRazorpayBalance();
                                        } catch (err) {
                                          alert('Failed to retry refund: ' + (err.response?.data?.detail || err.message));
                                        }
                                      }}
                                    >
                                      🔄 Retry Razorpay
                                    </button>
                                  )}
                                  {r.status !== 'awaiting_completion' && (
                                  <button
                                    className="complete-btn"
                                    onClick={async () => {
                                      const refundAmt = Number(r.net_refund || r.amount || 0);
                                      const utr = window.prompt(
                                        `Enter the bank UTR / transaction reference for the manual refund of ₹${refundAmt.toLocaleString()}.\n\nRequired — this is the audit trail for the offline transfer.`
                                      );
                                      if (utr === null) return;
                                      const utrTrimmed = (utr || '').trim();
                                      if (!utrTrimmed) {
                                        alert('UTR / reference is required to mark a refund as manually completed.');
                                        return;
                                      }
                                      const note = window.prompt(
                                        'Optional note (e.g. "Bank transfer via SBI on 25 Apr"). Press OK to skip.'
                                      ) || '';
                                      if (!window.confirm(
                                        `Mark refund of ₹${refundAmt.toLocaleString()} for "${r.campaign_name}" as completed with UTR ${utrTrimmed}?`
                                      )) return;
                                      try {
                                        await processAdminRefund({
                                          refund_id: r.refund_id,
                                          action: 'complete',
                                          utr_reference: utrTrimmed,
                                          notes: note,
                                        });
                                        alert('✅ Refund marked as completed.');
                                        fetchData();
                                      } catch (err) {
                                        alert('Failed to complete refund: ' + (err.response?.data?.detail || err.message));
                                      }
                                    }}
                                  >
                                    ✅ Mark Complete
                                  </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </>
                  )}

                  {/* Completed Refunds */}
                  {refundFilter === 'completed' && (
                    <>
                      {(!pendingRefunds.completed_refunds || pendingRefunds.completed_refunds.length === 0) ? (
                        <div className="empty-state">
                          <span>📭</span>
                          <p>No completed refunds yet.</p>
                        </div>
                      ) : (
                        <div className="refunds-list">
                          {pendingRefunds.completed_refunds.map(r => (
                            <div key={r.refund_id} className="refund-item status-completed">
                              <div className="refund-header">
                                <div className="refund-info">
                                  <h4>{r.campaign_name}</h4>
                                  <span className="brand">{r.brand_name}</span>
                                  <span className="email">{r.brand_email}</span>
                                </div>
                                <div className="refund-status">
                                  <span className="status-badge completed">✅ Completed</span>
                                  {r.completed_at && (
                                    <span className="days-pending">
                                      {new Date(r.completed_at).toLocaleDateString()}
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="refund-details">
                                <div className="detail-row">
                                  <div className="detail-item">
                                    <span className="label">Escrow Amount</span>
                                    <span className="value">₹{(r.escrow_amount || 0).toLocaleString()}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="label">Platform Fee (Retained)</span>
                                    <span className="value negative">-₹{(r.platform_fee || 0).toLocaleString()}</span>
                                  </div>
                                  <div className="detail-item">
                                    <span className="label">To Creators (Kill Fees)</span>
                                    <span className="value negative">-₹{(r.total_to_creators || 0).toLocaleString()}</span>
                                  </div>
                                  <div className="detail-item highlight">
                                    <span className="label">Net Refund to Brand</span>
                                    <span className="value">₹{(r.net_refund || r.amount || 0).toLocaleString()}</span>
                                  </div>
                                </div>

                                {r.razorpay_refund_id && (
                                  <div className="razorpay-info" style={{ marginTop: '12px' }}>
                                    <span className="label">Razorpay ID:</span>
                                    <span className="value" style={{ fontFamily: 'monospace' }}>{r.razorpay_refund_id}</span>
                                  </div>
                                )}

                                {r.utr_reference && (
                                  <div className="razorpay-info" style={{ marginTop: '12px' }}>
                                    <span className="label">UTR/Reference:</span>
                                    <span className="value" style={{ fontFamily: 'monospace' }}>{r.utr_reference}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            )}

            {/* USER MANAGEMENT SECTION */}
            {activeSection === 'user-management' && (
              <div className="user-management-section">
                <div className="section-row">
                  <div className="section-card">
                    <h3>👤 User Actions</h3>
                    <p className="section-description">Verify, suspend, or manage user accounts.</p>
                    <div className="quick-actions">
                      <button onClick={() => setActiveSection('creators-list')}>Manage Creators →</button>
                      <button onClick={() => setActiveSection('brands-list')}>Manage Brands →</button>
                    </div>
                  </div>

                  <div className="section-card">
                    <h3>🚫 Suspended Users ({suspendedUsers.length})</h3>
                    {suspendedUsers.length === 0 ? (
                      <p className="no-data">No suspended users.</p>
                    ) : (
                      <div className="suspended-list">
                        {suspendedUsers.map(u => (
                          <div key={u.id} className="suspended-item">
                            <div className="user-info">
                              <span className="name">{u.name}</span>
                              <span className="type">{u.type}</span>
                              <span className="email">{u.email}</span>
                            </div>
                            <div className="suspension-info">
                              <span className="reason">{u.reason}</span>
                              <span className="date">{formatDate(u.suspended_at)}</span>
                            </div>
                            <button 
                              className="unsuspend-btn"
                              onClick={() => openUserActionModal(u, u.type)}
                            >
                              Unsuspend
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* DATABASE EXPLORER SECTION */}
            {activeSection === 'database' && (
              <div className="database-section">
                {/* Collection Selector */}
                <div className="db-controls">
                  <div className="collection-selector">
                    <label>Select Collection:</label>
                    <select
                      value={selectedCollection}
                      onChange={(e) => {
                        setSelectedCollection(e.target.value);
                        setDbPage(1);
                        setDbSearchInput('');
                        setDbSearchApplied('');
                        setDbSearchField('');
                        setExpandedDoc(null);
                      }}
                    >
                      <option value="">-- Choose a collection --</option>
                      {dbCollections.map(col => (
                        <option key={col.name} value={col.name}>
                          {col.display_name} ({col.document_count.toLocaleString()} docs)
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedCollection && (
                    <div className="db-search-controls">
                      <select
                        value={dbSearchField}
                        onChange={(e) => setDbSearchField(e.target.value)}
                        className="field-select"
                      >
                        <option value="">All Fields</option>
                        {dbSearchableFields.map(field => (
                          <option key={field} value={field}>{field}</option>
                        ))}
                      </select>
                      <input
                        type="text"
                        placeholder="Search documents..."
                        value={dbSearchInput}
                        onChange={(e) => setDbSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setDbSearchApplied(dbSearchInput);
                            setDbPage(1);
                          }
                        }}
                        className="db-search-input"
                      />
                      <button 
                        onClick={() => {
                          setDbSearchApplied(dbSearchInput);
                          setDbPage(1);
                        }}
                        className="search-btn"
                      >
                        🔍 Search
                      </button>
                      {dbSearchApplied && (
                        <button 
                          onClick={() => {
                            setDbSearchInput('');
                            setDbSearchApplied('');
                            setDbPage(1);
                          }}
                          className="clear-btn"
                        >
                          ✕ Clear
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Search Info */}
                {dbSearchApplied && (
                  <div className="search-info">
                    Searching {dbSearchField ? `in "${dbSearchField}"` : 'all fields'} for: <strong>"{dbSearchApplied}"</strong>
                  </div>
                )}

                {/* Collection Stats */}
                {!selectedCollection && dbCollections.length > 0 && (
                  <div className="collections-grid">
                    {dbCollections.map(col => (
                      <div 
                        key={col.name} 
                        className="collection-card"
                        onClick={() => setSelectedCollection(col.name)}
                      >
                        <div className="collection-icon">🗄️</div>
                        <div className="collection-info">
                          <h4>{col.display_name}</h4>
                          <span className="doc-count">{col.document_count.toLocaleString()} documents</span>
                          <span className="collection-name">{col.name}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Documents List */}
                {selectedCollection && dbDocuments.length > 0 && (
                  <div className="documents-list">
                    <div className="documents-header">
                      <h3>{dbCollections.find(c => c.name === selectedCollection)?.display_name}</h3>
                      <span className="doc-count-badge">
                        {dbPagination?.total.toLocaleString()} documents
                      </span>
                    </div>

                    {dbDocuments.map((doc, index) => (
                      <div key={doc._id || index} className="document-card">
                        <div 
                          className="document-header"
                          onClick={() => setExpandedDoc(expandedDoc === doc._id ? null : doc._id)}
                        >
                          <span className="doc-id">
                            <strong>_id:</strong> {doc._id}
                          </span>
                          <span className="doc-preview">
                            {Object.entries(doc).slice(1, 4).map(([key, value]) => (
                              <span key={key} className="preview-field">
                                <span className="field-key">{key}:</span> 
                                <span className="field-value">
                                  {typeof value === 'object' ? JSON.stringify(value).substring(0, 30) + '...' : String(value).substring(0, 30)}
                                </span>
                              </span>
                            ))}
                          </span>
                          <button className="expand-btn">
                            {expandedDoc === doc._id ? '▲' : '▼'}
                          </button>
                        </div>
                        
                        {expandedDoc === doc._id && (
                          <div className="document-body">
                            <pre>{JSON.stringify(doc, null, 2)}</pre>
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Pagination */}
                    {dbPagination && dbPagination.pages > 1 && (
                      <div className="pagination">
                        <button 
                          disabled={dbPage <= 1}
                          onClick={() => setDbPage(p => p - 1)}
                        >
                          Previous
                        </button>
                        <span>Page {dbPage} of {dbPagination.pages}</span>
                        <button 
                          disabled={dbPage >= dbPagination.pages}
                          onClick={() => setDbPage(p => p + 1)}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* No documents */}
                {selectedCollection && dbDocuments.length === 0 && !loading && (
                  <div className="empty-state">
                    <span>📭</span>
                    <p>No documents found{dbSearchApplied ? ` for "${dbSearchApplied}"` : ''}.</p>
                  </div>
                )}
              </div>
            )}

            {/* ============================================================
                PERFORMANCE HUB ADMIN SECTION (additive)
                Backend: /api/admin/performance-hub/* (new isolated module)
                ============================================================ */}
            {activeSection === 'performance-hub' && (
              <div className="ph-admin-section">
                {/* Sub-tab nav */}
                <div className="ph-admin-subtabs" role="tablist" aria-label="Performance Hub admin sub-sections">
                  {[
                    { id: 'overview', label: 'Overview', icon: '📊' },
                    { id: 'campaigns', label: 'Campaigns', icon: '📁' },
                    { id: 'unconnected', label: 'Unconnected creators', icon: '📤' },
                    { id: 'brands', label: 'Brands & plans', icon: '💎' },
                    { id: 'abandoned', label: 'Abandoned checkouts', icon: '🛒' },
                  ].map((st) => (
                    <button
                      key={st.id}
                      type="button"
                      role="tab"
                      aria-selected={phSubTab === st.id}
                      className={`ph-admin-subtab ${phSubTab === st.id ? 'active' : ''}`}
                      onClick={() => setPhSubTab(st.id)}
                    >
                      <span aria-hidden>{st.icon}</span> {st.label}
                    </button>
                  ))}
                  <div className="ph-admin-subtabs-spacer" />
                  <button
                    type="button"
                    className="ph-admin-export-btn"
                    onClick={() => {
                      if (phSubTab === 'overview') downloadPhCsv('overview');
                      else if (phSubTab === 'campaigns')
                        downloadPhCsv('campaigns', {
                          status: phCampaignStatusFilter,
                          search: phCampaignSearchApplied || undefined,
                          brand_id: phFilterBrandId || undefined,
                          stale_only: phStaleCampaignsOnly
                        });
                      else if (phSubTab === 'unconnected')
                        downloadPhCsv('unconnected', {
                          status: phUnconnectedStatus,
                          search: phUnconnectedSearchApplied || undefined,
                          campaign_id: phFilterCampaignId || undefined,
                          brand_id: phFilterBrandId || undefined
                        });
                      else if (phSubTab === 'brands')
                        downloadPhCsv('brands', {
                          plan: phBrandPlanFilter,
                          search: phBrandSearchApplied || undefined,
                          renew_within_days: phBrandRenewWithinDays,
                          near_cap_only: phBrandNearCapOnly
                        });
                      else if (phSubTab === 'abandoned') downloadPhCsv('abandoned_checkouts');
                    }}
                  >
                    ⬇ Export CSV
                  </button>
                  <label className="ph-admin-granularity-label">
                    Granularity:
                    <select
                      className="ph-admin-granularity"
                      value={phGranularity}
                      onChange={(e) => setPhGranularity(e.target.value)}
                    >
                      <option value="day">Day</option>
                      <option value="week">Week</option>
                      <option value="month">Month</option>
                      <option value="year">Year</option>
                    </select>
                  </label>
                </div>

                {/* ----- OVERVIEW ----- */}
                {phSubTab === 'overview' && phOverview && (
                  <>
                    <div className="stats-grid">
                      <div className="stat-card primary">
                        <div className="stat-icon">💰</div>
                        <div className="stat-info">
                          <span className="stat-value">
                            ₹{phOverview.revenue?.total_collected_inr_all_time?.toLocaleString() || 0}
                          </span>
                          <span className="stat-label">Total collections (all time)</span>
                          <span className="stat-sub">
                            {phOverview.revenue?.count_orders_all_time || 0} paid orders
                          </span>
                        </div>
                      </div>
                      <div className="stat-card success">
                        <div className="stat-icon">📈</div>
                        <div className="stat-info">
                          <span className="stat-value">
                            ₹{phOverview.revenue?.in_period_inr?.toLocaleString() || 0}
                          </span>
                          <span className="stat-label">Revenue in selected period</span>
                          <span className="stat-sub">
                            {phOverview.revenue?.count_orders_in_period || 0} orders
                          </span>
                        </div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-icon">🟢</div>
                        <div className="stat-info">
                          <span className="stat-value">{phOverview.campaigns?.monitoring || 0}</span>
                          <span className="stat-label">Live campaigns</span>
                          <span className="stat-sub">{phOverview.campaigns?.total || 0} total</span>
                        </div>
                      </div>
                      <div className="stat-card">
                        <div className="stat-icon">👥</div>
                        <div className="stat-info">
                          <span className="stat-value">
                            {phOverview.creators?.connected || 0} / {phOverview.creators?.total_rows || 0}
                          </span>
                          <span className="stat-label">Creators connected Instagram</span>
                          <span className="stat-sub">
                            {phOverview.creators?.connection_rate_pct ?? 0}% connection rate
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Plans breakdown */}
                    <div className="section-row">
                      <div className="section-card">
                        <h3>💎 Plan distribution (brands)</h3>
                        <div className="ph-admin-plan-grid">
                          <div className="ph-admin-plan-card">
                            <span className="ph-admin-plan-label">Free</span>
                            <span className="ph-admin-plan-count">{phOverview.plans?.free?.count || 0}</span>
                            <span className="ph-admin-plan-sub">brands</span>
                          </div>
                          <div className="ph-admin-plan-card ph-admin-plan-paid">
                            <span className="ph-admin-plan-label">Pro</span>
                            <span className="ph-admin-plan-count">{phOverview.plans?.pro?.count || 0}</span>
                            <span className="ph-admin-plan-sub">
                              ₹{phOverview.plans?.pro?.revenue_inr?.toLocaleString() || 0} collected
                            </span>
                            <span className="ph-admin-plan-sub">
                              {phOverview.plans?.pro?.active_subscriptions || 0} active subs
                            </span>
                          </div>
                          <div className="ph-admin-plan-card ph-admin-plan-enterprise">
                            <span className="ph-admin-plan-label">Enterprise</span>
                            <span className="ph-admin-plan-count">
                              {phOverview.plans?.enterprise?.count || 0}
                            </span>
                            <span className="ph-admin-plan-sub">
                              ₹{phOverview.plans?.enterprise?.revenue_inr?.toLocaleString() || 0} collected
                            </span>
                            <span className="ph-admin-plan-sub">
                              {phOverview.plans?.enterprise?.active_subscriptions || 0} active subs
                            </span>
                          </div>
                          <div className="ph-admin-plan-card">
                            <span className="ph-admin-plan-label">Admin grants</span>
                            <span className="ph-admin-plan-count">
                              {phOverview.plans?.admin_grant?.count || 0}
                            </span>
                            <span className="ph-admin-plan-sub">never expire</span>
                          </div>
                          {(phOverview.plans?.paid_custom?.count || 0) > 0 && (
                            <div className="ph-admin-plan-card">
                              <span className="ph-admin-plan-label">Paid (custom caps)</span>
                              <span className="ph-admin-plan-count">
                                {phOverview.plans.paid_custom.count}
                              </span>
                              <span className="ph-admin-plan-sub">non-catalog</span>
                              <span className="ph-admin-plan-sub">
                                {phOverview.plans.paid_custom.active_subscriptions || 0} active subs
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="section-card">
                        <h3>📁 Campaign status</h3>
                        <div className="ph-admin-status-grid">
                          <div className="ph-admin-status-row">
                            <span className="status-badge monitoring">Live</span>
                            <strong>{phOverview.campaigns?.monitoring || 0}</strong>
                          </div>
                          <div className="ph-admin-status-row">
                            <span className="status-badge completed">Completed</span>
                            <strong>{phOverview.campaigns?.completed || 0}</strong>
                          </div>
                          <div className="ph-admin-status-row">
                            <span className="status-badge cancelled">Cancelled</span>
                            <strong>{phOverview.campaigns?.cancelled || 0}</strong>
                          </div>
                          <div className="ph-admin-status-row">
                            <span className="status-badge draft">Draft</span>
                            <strong>{phOverview.campaigns?.draft || 0}</strong>
                          </div>
                          <div className="ph-admin-status-row">
                            <span className="status-badge archived">Archived</span>
                            <strong>{phOverview.campaigns?.archived || 0}</strong>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Creator funnel */}
                    <div className="section-card full-width">
                      <h3>📤 Invite → connect funnel</h3>
                      <div className="ph-admin-funnel">
                        <div className="ph-admin-funnel-step">
                          <span className="ph-admin-funnel-num">
                            {phOverview.creators?.total_rows || 0}
                          </span>
                          <span className="ph-admin-funnel-lbl">Total invites sent</span>
                        </div>
                        <div className="ph-admin-funnel-arrow">→</div>
                        <div className="ph-admin-funnel-step">
                          <span className="ph-admin-funnel-num">
                            {phOverview.creators?.connected || 0}
                          </span>
                          <span className="ph-admin-funnel-lbl">Connected Instagram</span>
                        </div>
                        <div className="ph-admin-funnel-arrow">·</div>
                        <div className="ph-admin-funnel-step ph-admin-funnel-warn">
                          <span className="ph-admin-funnel-num">
                            {phOverview.creators?.pending || 0}
                          </span>
                          <span className="ph-admin-funnel-lbl">Pending (no action)</span>
                        </div>
                        <div className="ph-admin-funnel-arrow">·</div>
                        <div className="ph-admin-funnel-step ph-admin-funnel-warn">
                          <span className="ph-admin-funnel-num">
                            {phOverview.creators?.connecting || 0}
                          </span>
                          <span className="ph-admin-funnel-lbl">In progress</span>
                        </div>
                        <div className="ph-admin-funnel-arrow">·</div>
                        <div className="ph-admin-funnel-step ph-admin-funnel-bad">
                          <span className="ph-admin-funnel-num">
                            {phOverview.creators?.failed || 0}
                          </span>
                          <span className="ph-admin-funnel-lbl">Failed</span>
                        </div>
                      </div>
                      <p className="ph-admin-cta-hint">
                        Use the <strong>Unconnected creators</strong> tab to get a manual outreach worklist
                        (filter by status, search by handle or email).
                      </p>
                    </div>

                    {phOverview.ops && (
                      <div className="section-card full-width">
                        <h3>⚙️ Renewals, MRR snapshot & platform health</h3>
                        <p className="ph-admin-muted">
                          {phOverview.ops.mrr_indicative_note}
                        </p>
                        <div className="stats-grid small">
                          <div className="stat-card primary">
                            <span className="stat-value">
                              ₹{(phOverview.ops.mrr_indicative_inr || 0).toLocaleString()}
                            </span>
                            <span className="stat-label">Indicative MRR (catalog Pro + Ent)</span>
                          </div>
                          <div className="stat-card">
                            <span className="stat-value">
                              {phOverview.ops.renewals_ending_within_days?.['7'] ?? 0} /{' '}
                              {phOverview.ops.renewals_ending_within_days?.['14'] ?? 0} /{' '}
                              {phOverview.ops.renewals_ending_within_days?.['30'] ?? 0}
                            </span>
                            <span className="stat-label">Renewals in 7 / 14 / 30 days</span>
                          </div>
                          <div className="stat-card">
                            <span className="stat-value">
                              {phOverview.ops.lapsed_paid_brands_count ?? 0}
                            </span>
                            <span className="stat-label">Lapsed paid (win-back)</span>
                          </div>
                          <div className="stat-card ph-admin-funnel-warn">
                            <span className="stat-value">
                              {phOverview.ops.stale_live_campaigns_count ?? 0}
                            </span>
                            <span className="stat-label">
                              Stale live campaigns (no sync ≥{' '}
                              {phOverview.ops.stale_sync_hours_threshold ?? 36}h)
                            </span>
                          </div>
                          <div className="stat-card">
                            <span className="stat-value">
                              {phOverview.ops.repeat_purchaser_brand_count ?? 0}
                            </span>
                            <span className="stat-label">Repeat-purchase brands</span>
                          </div>
                          <div className="stat-card">
                            <span className="stat-value">
                              {phOverview.ops.abandoned_checkouts_open_count ?? 0}
                            </span>
                            <span className="stat-label">Open abandoned checkouts</span>
                          </div>
                        </div>
                        {phOverview.ops.platform_engagement_latest_snapshot_totals && (
                          <div className="ph-admin-engagement-strip">
                            <strong>Latest snapshot totals (all PH rows)</strong>
                            <span>
                              Likes{' '}
                              {(
                                phOverview.ops.platform_engagement_latest_snapshot_totals.likes || 0
                              ).toLocaleString()}
                            </span>
                            <span>
                              Comments{' '}
                              {(
                                phOverview.ops.platform_engagement_latest_snapshot_totals.comments ||
                                0
                              ).toLocaleString()}
                            </span>
                            <span>
                              Reach{' '}
                              {(
                                phOverview.ops.platform_engagement_latest_snapshot_totals.reach || 0
                              ).toLocaleString()}
                            </span>
                            <span>
                              Rows tracked{' '}
                              {phOverview.ops.platform_engagement_latest_snapshot_totals
                                .tracked_rows || 0}
                            </span>
                          </div>
                        )}
                        {(phOverview.ops.stale_live_campaigns_sample || []).length > 0 && (
                          <div className="data-table-container ph-admin-stale-table">
                            <table className="data-table">
                              <thead>
                                <tr>
                                  <th>Campaign</th>
                                  <th>Brand</th>
                                  <th>Last sync</th>
                                  <th />
                                </tr>
                              </thead>
                              <tbody>
                                {phOverview.ops.stale_live_campaigns_sample.map((s) => (
                                  <tr key={s.id}>
                                    <td>{s.name || '—'}</td>
                                    <td>{s.brand?.name || '—'}</td>
                                    <td>{s.last_sync_at ? formatDateTime(s.last_sync_at) : '—'}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="search-btn"
                                        onClick={() => {
                                          setPhFilterBrandId(s.brand?.id || '');
                                          setPhStaleCampaignsOnly(true);
                                          setPhCampaignStatusFilter('live');
                                          setPhSubTab('campaigns');
                                          setPhCampaignPage(1);
                                        }}
                                      >
                                        Open in Campaigns
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Time breakdown */}
                    <div className="section-card full-width">
                      <h3>📅 Time breakdown ({phGranularity})</h3>
                      {(phOverview.time_breakdown || []).length === 0 ? (
                        <p className="no-data">No activity in the selected period.</p>
                      ) : (
                        <div className="data-table-container">
                          <table className="data-table">
                            <thead>
                              <tr>
                                <th>{phGranularity === 'day' ? 'Day' : phGranularity}</th>
                                <th>New paid subs</th>
                                <th>Revenue (₹)</th>
                                <th>New campaigns</th>
                                <th>Invites sent</th>
                                <th>Connections</th>
                              </tr>
                            </thead>
                            <tbody>
                              {phOverview.time_breakdown.map((b) => (
                                <tr key={b.bucket}>
                                  <td>{b.bucket}</td>
                                  <td>{b.new_subscriptions_count}</td>
                                  <td>₹{(b.new_subscriptions_inr || 0).toLocaleString()}</td>
                                  <td>{b.new_campaigns}</td>
                                  <td>{b.new_invites_sent}</td>
                                  <td>{b.new_creators_connected}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* ----- CAMPAIGNS ----- */}
                {phSubTab === 'campaigns' && (
                  <>
                    <div className="list-controls">
                      <select
                        value={phCampaignStatusFilter}
                        onChange={(e) => {
                          setPhCampaignStatusFilter(e.target.value);
                          setPhCampaignPage(1);
                        }}
                        className="ph-admin-filter-select"
                      >
                        <option value="all">All statuses</option>
                        <option value="live">Live (monitoring)</option>
                        <option value="draft">Draft</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                        <option value="archived">Archived</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Search by campaign name…"
                        value={phCampaignSearchInput}
                        onChange={(e) => setPhCampaignSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setPhCampaignSearchApplied(phCampaignSearchInput);
                            setPhCampaignPage(1);
                          }
                        }}
                        className="search-input"
                      />
                      <button
                        type="button"
                        className="search-btn"
                        onClick={() => {
                          setPhCampaignSearchApplied(phCampaignSearchInput);
                          setPhCampaignPage(1);
                        }}
                      >
                        🔍 Search
                      </button>
                      {phCampaignSearchApplied && (
                        <button
                          type="button"
                          className="clear-btn"
                          onClick={() => {
                            setPhCampaignSearchInput('');
                            setPhCampaignSearchApplied('');
                            setPhCampaignPage(1);
                          }}
                        >
                          ✕ Clear
                        </button>
                      )}
                    </div>
                    {phCampaignSearchApplied && (
                      <div className="search-info">
                        Showing results for: <strong>"{phCampaignSearchApplied}"</strong>
                      </div>
                    )}
                    {(phFilterBrandId || phStaleCampaignsOnly) && (
                      <div className="search-info ph-admin-filter-banner">
                        {phFilterBrandId && (
                          <>
                            Filtered by brand ID <code>{phFilterBrandId}</code>{' '}
                            <button
                              type="button"
                              className="clear-btn"
                              onClick={() => {
                                setPhFilterBrandId('');
                                setPhCampaignPage(1);
                              }}
                            >
                              Clear brand
                            </button>
                          </>
                        )}
                        {phStaleCampaignsOnly && (
                          <span style={{ marginLeft: 8 }}>
                            Showing stale live campaigns only{' '}
                            <button
                              type="button"
                              className="clear-btn"
                              onClick={() => {
                                setPhStaleCampaignsOnly(false);
                                setPhCampaignPage(1);
                              }}
                            >
                              Clear
                            </button>
                          </span>
                        )}
                      </div>
                    )}

                    <div className="list-controls ph-admin-campaign-extra">
                      <label className="ph-admin-checkbox">
                        <input
                          type="checkbox"
                          checked={phStaleCampaignsOnly}
                          onChange={(e) => {
                            setPhStaleCampaignsOnly(e.target.checked);
                            setPhCampaignPage(1);
                          }}
                        />
                        Stale live only (old / missing sync)
                      </label>
                    </div>

                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Campaign</th>
                            <th>Brand</th>
                            <th>Status</th>
                            <th>Window</th>
                            <th>Creators (connected / total)</th>
                            <th>Connection rate</th>
                            <th>Last sync</th>
                            <th>Created</th>
                            <th>Links</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {phCampaigns.list.length === 0 && (
                            <tr>
                              <td colSpan={10}>
                                <p className="no-data">No campaigns match the current filters.</p>
                              </td>
                            </tr>
                          )}
                          {phCampaigns.list.map((c) => (
                            <tr key={c.id}>
                              <td className="name-cell">{c.name || 'Untitled'}</td>
                              <td>
                                <div>{c.brand?.name || 'Unknown'}</div>
                                {c.brand?.email && (
                                  <div className="email-cell" style={{ fontSize: 12, color: '#64748b' }}>
                                    {c.brand.email}
                                  </div>
                                )}
                                {c.brand?.id && (
                                  <button
                                    type="button"
                                    className="ph-admin-linkish"
                                    onClick={() => {
                                      setPhFilterBrandId(c.brand.id);
                                      setPhCampaignPage(1);
                                      setPhSubTab('campaigns');
                                    }}
                                  >
                                    All PH campaigns
                                  </button>
                                )}
                              </td>
                              <td>
                                <span className={`status-badge ${c.status}`}>{c.status}</span>
                              </td>
                              <td>
                                {(c.monitoring_unit || 'weeks') === 'days' && c.monitoring_days != null
                                  ? `${c.monitoring_days} days`
                                  : c.monitoring_weeks != null
                                    ? `${c.monitoring_weeks} weeks`
                                    : '—'}
                                {c.monitoring_ends_at && (
                                  <div style={{ fontSize: 12, color: '#64748b' }}>
                                    until {formatDate(c.monitoring_ends_at)}
                                  </div>
                                )}
                              </td>
                              <td>
                                <strong>{c.rows?.connected ?? 0}</strong> / {c.rows?.total ?? 0}
                                {(c.rows?.failed || 0) > 0 && (
                                  <span style={{ marginLeft: 6, color: '#b91c1c', fontSize: 12 }}>
                                    ({c.rows.failed} failed)
                                  </span>
                                )}
                              </td>
                              <td>{c.rows?.connection_rate_pct ?? 0}%</td>
                              <td style={{ fontSize: 12 }}>
                                {c.last_sync_at ? formatDateTime(c.last_sync_at) : '—'}
                              </td>
                              <td>{formatDate(c.created_at)}</td>
                              <td>
                                <button
                                  type="button"
                                  className="ph-admin-linkish"
                                  onClick={() => {
                                    setPhFilterCampaignId(c.id);
                                    setPhUnconnectedPage(1);
                                    setPhSubTab('unconnected');
                                  }}
                                >
                                  Unconnected
                                </button>
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="search-btn"
                                  onClick={async () => {
                                    setPhCampaignDetail(null);
                                    setPhCampaignDetailLoading(true);
                                    try {
                                      const res = await perfHubAdminCampaignDetail(c.id);
                                      if (res.data?.success) {
                                        setPhCampaignDetail(res.data);
                                      }
                                    } catch (err) {
                                      alert(
                                        err.response?.data?.detail ||
                                          'Could not load campaign detail.'
                                      );
                                    } finally {
                                      setPhCampaignDetailLoading(false);
                                    }
                                  }}
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {phCampaigns.pagination && phCampaigns.pagination.pages > 1 && (
                      <div className="pagination">
                        <button
                          disabled={phCampaignPage <= 1}
                          onClick={() => setPhCampaignPage((p) => p - 1)}
                        >
                          Previous
                        </button>
                        <span>
                          Page {phCampaignPage} of {phCampaigns.pagination.pages} ·{' '}
                          {phCampaigns.pagination.total} campaigns
                        </span>
                        <button
                          disabled={phCampaignPage >= phCampaigns.pagination.pages}
                          onClick={() => setPhCampaignPage((p) => p + 1)}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}

                {/* ----- UNCONNECTED CREATORS WORKLIST ----- */}
                {phSubTab === 'unconnected' && (
                  <>
                    {phUnconnected.summary && (
                      <div className="stats-grid small">
                        <div className="stat-card">
                          <span className="stat-value">{phUnconnected.summary.pending || 0}</span>
                          <span className="stat-label">Pending (no action)</span>
                        </div>
                        <div className="stat-card">
                          <span className="stat-value">{phUnconnected.summary.connecting || 0}</span>
                          <span className="stat-label">In progress</span>
                        </div>
                        <div className="stat-card">
                          <span className="stat-value">{phUnconnected.summary.failed || 0}</span>
                          <span className="stat-label">Failed</span>
                        </div>
                      </div>
                    )}

                    {phFilterCampaignId && (
                      <div className="search-info ph-admin-filter-banner">
                        Unconnected list filtered by campaign{' '}
                        <code>{phFilterCampaignId}</code>{' '}
                        <button
                          type="button"
                          className="clear-btn"
                          onClick={() => {
                            setPhFilterCampaignId('');
                            setPhUnconnectedPage(1);
                          }}
                        >
                          Clear campaign
                        </button>
                      </div>
                    )}

                    {phFilterBrandId && (
                      <div className="search-info ph-admin-filter-banner">
                        Unconnected list filtered by brand{' '}
                        <code>{phFilterBrandId}</code>{' '}
                        <button
                          type="button"
                          className="clear-btn"
                          onClick={() => {
                            setPhFilterBrandId('');
                            setPhUnconnectedPage(1);
                          }}
                        >
                          Clear brand
                        </button>
                      </div>
                    )}

                    {(phUnconnected.errorBreakdown || []).length > 0 && (
                      <div className="section-card full-width">
                        <h4>Failure reasons (all failed invites)</h4>
                        <div className="ph-admin-error-chips">
                          {phUnconnected.errorBreakdown.map((x) => (
                            <span key={x.bucket} className="ph-admin-chip">
                              {x.bucket}: <strong>{x.count}</strong>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="list-controls">
                      <select
                        value={phUnconnectedStatus}
                        onChange={(e) => {
                          setPhUnconnectedStatus(e.target.value);
                          setPhUnconnectedPage(1);
                        }}
                        className="ph-admin-filter-select"
                      >
                        <option value="all_unconnected">All unconnected</option>
                        <option value="pending">Pending</option>
                        <option value="connecting">Connecting</option>
                        <option value="failed">Failed</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Search handle or email…"
                        value={phUnconnectedSearchInput}
                        onChange={(e) => setPhUnconnectedSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setPhUnconnectedSearchApplied(phUnconnectedSearchInput);
                            setPhUnconnectedPage(1);
                          }
                        }}
                        className="search-input"
                      />
                      <button
                        type="button"
                        className="search-btn"
                        onClick={() => {
                          setPhUnconnectedSearchApplied(phUnconnectedSearchInput);
                          setPhUnconnectedPage(1);
                        }}
                      >
                        🔍 Search
                      </button>
                      {phUnconnectedSearchApplied && (
                        <button
                          type="button"
                          className="clear-btn"
                          onClick={() => {
                            setPhUnconnectedSearchInput('');
                            setPhUnconnectedSearchApplied('');
                            setPhUnconnectedPage(1);
                          }}
                        >
                          ✕ Clear
                        </button>
                      )}
                    </div>

                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Handle</th>
                            <th>Email</th>
                            <th>Post URL</th>
                            <th>Status</th>
                            <th>Bucket</th>
                            <th>Campaign</th>
                            <th>Brand</th>
                            <th>Last invite</th>
                            <th>Resends</th>
                            <th>Error</th>
                          </tr>
                        </thead>
                        <tbody>
                          {phUnconnected.rows.length === 0 && (
                            <tr>
                              <td colSpan={10}>
                                <p className="no-data">
                                  No unconnected creators match the current filters.
                                </p>
                              </td>
                            </tr>
                          )}
                          {phUnconnected.rows.map((r) => (
                            <tr key={r.row_id}>
                              <td className="name-cell">@{r.instagram_handle}</td>
                              <td>
                                {r.email ? (
                                  <a
                                    href={`mailto:${r.email}`}
                                    className="email-cell"
                                    style={{ color: '#2563eb' }}
                                  >
                                    {r.email}
                                  </a>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td style={{ maxWidth: 140, fontSize: 12 }}>
                                {r.post_url ? (
                                  <>
                                    <a href={r.post_url} target="_blank" rel="noopener noreferrer">
                                      Open
                                    </a>
                                    <button
                                      type="button"
                                      className="ph-admin-linkish"
                                      style={{ marginLeft: 6 }}
                                      onClick={() => {
                                        navigator.clipboard.writeText(r.post_url).catch(() => {});
                                      }}
                                    >
                                      Copy
                                    </button>
                                  </>
                                ) : (
                                  '—'
                                )}
                              </td>
                              <td>
                                <span className={`status-badge ${r.invite_status}`}>
                                  {r.invite_status}
                                </span>
                              </td>
                              <td style={{ fontSize: 12 }}>{r.connection_error_bucket || '—'}</td>
                              <td>
                                {r.campaign?.name || '—'}
                                {r.campaign?.status && (
                                  <div style={{ fontSize: 12, color: '#64748b' }}>
                                    {r.campaign.status}
                                  </div>
                                )}
                                {r.campaign?.id && (
                                  <button
                                    type="button"
                                    className="ph-admin-linkish"
                                    onClick={() => {
                                      setPhFilterCampaignId(r.campaign.id);
                                      setPhUnconnectedPage(1);
                                    }}
                                  >
                                    Filter this campaign
                                  </button>
                                )}
                              </td>
                              <td>
                                <div>{r.brand?.name || 'Unknown'}</div>
                                {r.brand?.email && (
                                  <div style={{ fontSize: 12, color: '#64748b' }}>
                                    {r.brand.email}
                                  </div>
                                )}
                                {r.brand?.id && (
                                  <button
                                    type="button"
                                    className="ph-admin-linkish"
                                    onClick={() => {
                                      setPhFilterBrandId(r.brand.id);
                                      setPhCampaignPage(1);
                                      setPhSubTab('campaigns');
                                    }}
                                  >
                                    PH campaigns
                                  </button>
                                )}
                              </td>
                              <td>{formatDateTime(r.last_invite_sent_at)}</td>
                              <td>{r.resend_count}</td>
                              <td style={{ maxWidth: 220, fontSize: 12, color: '#b91c1c' }}>
                                {r.connection_error || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {phUnconnected.pagination && phUnconnected.pagination.pages > 1 && (
                      <div className="pagination">
                        <button
                          disabled={phUnconnectedPage <= 1}
                          onClick={() => setPhUnconnectedPage((p) => p - 1)}
                        >
                          Previous
                        </button>
                        <span>
                          Page {phUnconnectedPage} of {phUnconnected.pagination.pages} ·{' '}
                          {phUnconnected.pagination.total} rows
                        </span>
                        <button
                          disabled={phUnconnectedPage >= phUnconnected.pagination.pages}
                          onClick={() => setPhUnconnectedPage((p) => p + 1)}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}

                {/* ----- BRANDS & PLANS ----- */}
                {phSubTab === 'brands' && (
                  <>
                    {phBrands.summary && (
                      <div className="stats-grid small">
                        <div className="stat-card">
                          <span className="stat-value">{phBrands.summary.free_count || 0}</span>
                          <span className="stat-label">Free</span>
                        </div>
                        <div className="stat-card">
                          <span className="stat-value">{phBrands.summary.pro_count || 0}</span>
                          <span className="stat-label">Pro</span>
                        </div>
                        <div className="stat-card">
                          <span className="stat-value">
                            {phBrands.summary.enterprise_count || 0}
                          </span>
                          <span className="stat-label">Enterprise</span>
                        </div>
                        <div className="stat-card">
                          <span className="stat-value">
                            {phBrands.summary.admin_grant_count || 0}
                          </span>
                          <span className="stat-label">Admin grants</span>
                        </div>
                        <div className="stat-card primary">
                          <span className="stat-value">
                            ₹{(phBrands.summary.total_revenue_inr || 0).toLocaleString()}
                          </span>
                          <span className="stat-label">Total revenue (in view)</span>
                        </div>
                      </div>
                    )}

                    <div className="list-controls">
                      <select
                        value={phBrandPlanFilter}
                        onChange={(e) => {
                          setPhBrandPlanFilter(e.target.value);
                          setPhBrandPage(1);
                        }}
                        className="ph-admin-filter-select"
                      >
                        <option value="all">All plans</option>
                        <option value="paid">Paid (Pro + Enterprise)</option>
                        <option value="pro">Pro</option>
                        <option value="enterprise">Enterprise</option>
                        <option value="paid_custom">Paid (custom caps)</option>
                        <option value="lapsed">Lapsed paid (win-back)</option>
                        <option value="renewing_soon">Renewing soon</option>
                        <option value="admin_grant">Admin grants</option>
                        <option value="free">Free</option>
                      </select>
                      {phBrandPlanFilter === 'renewing_soon' && (
                        <label className="ph-admin-granularity-label">
                          Renew within (days)
                          <select
                            className="ph-admin-granularity"
                            value={phBrandRenewWithinDays}
                            onChange={(e) => {
                              setPhBrandRenewWithinDays(Number(e.target.value));
                              setPhBrandPage(1);
                            }}
                          >
                            <option value={7}>7</option>
                            <option value={14}>14</option>
                            <option value={30}>30</option>
                          </select>
                        </label>
                      )}
                      <label className="ph-admin-checkbox">
                        <input
                          type="checkbox"
                          checked={phBrandNearCapOnly}
                          onChange={(e) => {
                            setPhBrandNearCapOnly(e.target.checked);
                            setPhBrandPage(1);
                          }}
                        />
                        Near cap only
                      </label>
                      <input
                        type="text"
                        placeholder="Search brand name or email…"
                        value={phBrandSearchInput}
                        onChange={(e) => setPhBrandSearchInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setPhBrandSearchApplied(phBrandSearchInput);
                            setPhBrandPage(1);
                          }
                        }}
                        className="search-input"
                      />
                      <button
                        type="button"
                        className="search-btn"
                        onClick={() => {
                          setPhBrandSearchApplied(phBrandSearchInput);
                          setPhBrandPage(1);
                        }}
                      >
                        🔍 Search
                      </button>
                      {phBrandSearchApplied && (
                        <button
                          type="button"
                          className="clear-btn"
                          onClick={() => {
                            setPhBrandSearchInput('');
                            setPhBrandSearchApplied('');
                            setPhBrandPage(1);
                          }}
                        >
                          ✕ Clear
                        </button>
                      )}
                    </div>

                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Brand</th>
                            <th>Email</th>
                            <th>Plan</th>
                            <th>Caps</th>
                            <th>Campaigns (active / total)</th>
                            <th>Creators connected</th>
                            <th>Total paid (₹)</th>
                            <th>Renews / ends</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {phBrands.list.length === 0 && (
                            <tr>
                              <td colSpan={9}>
                                <p className="no-data">No brands match the current filters.</p>
                              </td>
                            </tr>
                          )}
                          {phBrands.list.map((b) => (
                            <tr key={b.id}>
                              <td className="name-cell">{b.name}</td>
                              <td className="email-cell">{b.email || '—'}</td>
                              <td>
                                <span className={`status-badge ${b.plan}`}>{b.plan}</span>
                                {b.is_admin_grant && (
                                  <div style={{ fontSize: 11, color: '#7c3aed' }}>admin grant</div>
                                )}
                              </td>
                              <td>
                                {b.max_active_campaigns || 0} camps · {b.max_connected_creators || 0}{' '}
                                creators
                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                                  Usage {b.cap_use_campaigns_pct ?? 0}% camps ·{' '}
                                  {b.cap_use_creators_pct ?? 0}% creators
                                  {(b.near_cap_campaigns || b.near_cap_creators) && (
                                    <span className="ph-admin-cap-warn"> · Near cap</span>
                                  )}
                                </div>
                              </td>
                              <td>
                                {b.campaigns?.active ?? 0} / {b.campaigns?.total ?? 0}
                              </td>
                              <td>
                                {b.creators?.total_connected ?? 0} /{' '}
                                {b.creators?.total_invited ?? 0}
                              </td>
                              <td>₹{(b.total_paid_inr || 0).toLocaleString()}</td>
                              <td>
                                {b.current_period_end ? formatDate(b.current_period_end) : '—'}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="search-btn"
                                  style={{ marginBottom: 4, display: 'block' }}
                                  onClick={() => {
                                    setPhFilterBrandId(b.id);
                                    setPhCampaignPage(1);
                                    setPhSubTab('campaigns');
                                  }}
                                >
                                  Campaigns
                                </button>
                                <button
                                  type="button"
                                  className="search-btn"
                                  onClick={() => {
                                    setPhFilterBrandId(b.id);
                                    setPhUnconnectedPage(1);
                                    setPhSubTab('unconnected');
                                  }}
                                >
                                  Unconnected
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {phBrands.pagination && phBrands.pagination.pages > 1 && (
                      <div className="pagination">
                        <button
                          disabled={phBrandPage <= 1}
                          onClick={() => setPhBrandPage((p) => p - 1)}
                        >
                          Previous
                        </button>
                        <span>
                          Page {phBrandPage} of {phBrands.pagination.pages} ·{' '}
                          {phBrands.pagination.total} brands
                        </span>
                        <button
                          disabled={phBrandPage >= phBrands.pagination.pages}
                          onClick={() => setPhBrandPage((p) => p + 1)}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                )}

                {phSubTab === 'abandoned' && (
                  <>
                    <p className="ph-admin-muted">
                      Checkout orders that are not completed — follow up with the brand to close the
                      sale.
                    </p>
                    <div className="data-table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Brand</th>
                            <th>Status</th>
                            <th>Tier</th>
                            <th>Amount (₹)</th>
                            <th>Razorpay order</th>
                            <th>Created</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(phAbandoned.orders || []).length === 0 && (
                            <tr>
                              <td colSpan={6}>
                                <p className="no-data">No abandoned checkouts in this period.</p>
                              </td>
                            </tr>
                          )}
                          {(phAbandoned.orders || []).map((o) => (
                            <tr key={o.id}>
                              <td>
                                <div>{o.brand?.name || '—'}</div>
                                {o.brand?.email && (
                                  <div style={{ fontSize: 12, color: '#64748b' }}>{o.brand.email}</div>
                                )}
                                {o.brand?.id && (
                                  <button
                                    type="button"
                                    className="ph-admin-linkish"
                                    onClick={() => {
                                      setPhFilterBrandId(o.brand.id);
                                      setPhCampaignPage(1);
                                      setPhSubTab('campaigns');
                                    }}
                                  >
                                    PH campaigns
                                  </button>
                                )}
                              </td>
                              <td>
                                <span className={`status-badge ${o.status || 'pending'}`}>
                                  {o.status}
                                </span>
                              </td>
                              <td>{o.tier}</td>
                              <td>₹{(o.amount_inr || 0).toLocaleString()}</td>
                              <td style={{ fontSize: 12 }}>{o.razorpay_order_id || '—'}</td>
                              <td>{formatDateTime(o.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {phAbandoned.total > (phAbandoned.orders || []).length && (
                      <p className="ph-admin-muted">
                        Showing {(phAbandoned.orders || []).length} of {phAbandoned.total} open orders.
                        Use CSV export for a larger slice.
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Performance Hub — campaign detail modal (additive). Triggered from
          the View button in the PH Campaigns sub-tab. Closes on overlay click. */}
      {phCampaignDetail && (
        <div className="modal-overlay" onClick={() => setPhCampaignDetail(null)}>
          <div
            className="ph-admin-detail-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="modal-header">
              <h2>
                {phCampaignDetail.campaign?.name || 'Campaign'}{' '}
                <span className={`status-badge ${phCampaignDetail.campaign?.status}`}>
                  {phCampaignDetail.campaign?.status}
                </span>
              </h2>
              <button className="close-btn" onClick={() => setPhCampaignDetail(null)}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="ph-admin-detail-meta">
                <div>
                  <strong>Brand:</strong> {phCampaignDetail.brand?.name || 'Unknown'}
                  {phCampaignDetail.brand?.email && <> · {phCampaignDetail.brand.email}</>}
                </div>
                <div>
                  <strong>Monitoring window:</strong>{' '}
                  {phCampaignDetail.campaign &&
                  ((phCampaignDetail.campaign.monitoring_unit || 'weeks') === 'days' &&
                  phCampaignDetail.campaign.monitoring_days != null
                    ? `${phCampaignDetail.campaign.monitoring_days} days`
                    : phCampaignDetail.campaign.monitoring_weeks != null
                      ? `${phCampaignDetail.campaign.monitoring_weeks} weeks`
                      : '—')}
                  {phCampaignDetail.campaign?.monitoring_ends_at && (
                    <> · ends {formatDateTime(phCampaignDetail.campaign.monitoring_ends_at)}</>
                  )}
                </div>
                <div>
                  <strong>Created:</strong> {formatDateTime(phCampaignDetail.campaign?.created_at)}
                  {' · '}
                  <strong>Last sync:</strong>{' '}
                  {phCampaignDetail.campaign?.last_sync_at
                    ? formatDateTime(phCampaignDetail.campaign.last_sync_at)
                    : '—'}
                </div>
                <div>
                  <strong>Snapshots captured:</strong> {phCampaignDetail.snapshots_count}
                </div>
              </div>

              <h4 style={{ marginTop: 16 }}>Latest aggregated metrics</h4>
              <div className="ph-admin-detail-metrics">
                {Object.entries(phCampaignDetail.totals_latest || {}).map(([k, v]) => (
                  <div key={k} className="ph-admin-detail-metric">
                    <span className="ph-admin-detail-metric-label">{k}</span>
                    <span className="ph-admin-detail-metric-value">
                      {Number(v || 0).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>

              <h4 style={{ marginTop: 16 }}>
                Creator rows ({(phCampaignDetail.rows || []).length})
              </h4>
              <div className="data-table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Handle</th>
                      <th>Email</th>
                      <th>Status</th>
                      <th>Latest snapshot</th>
                      <th>Resends</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(phCampaignDetail.rows || []).map((r) => (
                      <tr key={r.id}>
                        <td className="name-cell">@{r.instagram_handle}</td>
                        <td className="email-cell">{r.email || '—'}</td>
                        <td>
                          <span className={`status-badge ${r.invite_status}`}>
                            {r.invite_status}
                          </span>
                        </td>
                        <td>
                          {r.latest_snapshot
                            ? `Likes ${r.latest_snapshot.likes || 0} · Comments ${
                                r.latest_snapshot.comments || 0
                              } · Reach ${r.latest_snapshot.reach || 0}`
                            : '—'}
                          {r.latest_snapshot?.captured_at && (
                            <div style={{ fontSize: 12, color: '#64748b' }}>
                              {formatDateTime(r.latest_snapshot.captured_at)}
                            </div>
                          )}
                        </td>
                        <td>{r.resend_count || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
      {phCampaignDetailLoading && (
        <div className="ph-admin-detail-loading-toast">Loading campaign detail…</div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedCreator && (
        <div className="modal-overlay" onClick={() => setShowPaymentModal(false)}>
          <div className="payment-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Record Payment</h2>
              <button className="close-btn" onClick={() => setShowPaymentModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="payment-summary-box">
                <h3>{selectedCreator.name}</h3>
                <p>@{selectedCreator.username}</p>
                {selectedCreator.installment && (
                  <div className="installment-summary" style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 13, color: '#475569' }}>
                      {selectedCreator.installment.label}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                      Gross ₹{selectedCreator.installment.gross?.toLocaleString()}
                      &nbsp;·&nbsp;Platform fee ₹{selectedCreator.installment.platform_fee?.toLocaleString()}
                    </div>
                  </div>
                )}
                <div className="amount-display">
                  ₹{(selectedCreator.installment?.net_payable ?? selectedCreator.net_payable)?.toLocaleString()}
                </div>
              </div>

              <div className="payment-details-box">
                <h4>Payment Details</h4>
                {selectedCreator.payment_details?.payment_method === 'upi' ? (
                  <div className="detail-row">
                    <span>UPI ID:</span>
                    <span className="copyable">{selectedCreator.payment_details.upi_id}</span>
                  </div>
                ) : (
                  <>
                    <div className="detail-row">
                      <span>Account:</span>
                      <span>{selectedCreator.payment_details?.account_number}</span>
                    </div>
                    <div className="detail-row">
                      <span>IFSC:</span>
                      <span>{selectedCreator.payment_details?.ifsc_code}</span>
                    </div>
                  </>
                )}
              </div>

              <form onSubmit={handleSubmitPayment}>
                <div className="form-group">
                  <label>Payment Method</label>
                  <select
                    value={paymentForm.payment_method}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_method: e.target.value }))}
                  >
                    <option value="upi">UPI</option>
                    <option value="neft">NEFT</option>
                    <option value="imps">IMPS</option>
                    <option value="rtgs">RTGS</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>UTR / Reference *</label>
                  <input
                    type="text"
                    placeholder="Enter UTR or reference number"
                    value={paymentForm.utr_reference}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, utr_reference: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Notes (Optional)</label>
                  <textarea
                    placeholder="Any notes..."
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm(prev => ({ ...prev, notes: e.target.value }))}
                    rows={2}
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowPaymentModal(false)}>Cancel</button>
                  <button type="submit" className="confirm-btn" disabled={submitting}>
                    {submitting
                      ? 'Processing...'
                      : `Confirm ₹${(selectedCreator.installment?.net_payable ?? selectedCreator.net_payable)?.toLocaleString()}`}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Campaign Control Modal */}
      {showCampaignControlModal && selectedCampaignForControl && (
        <div className="modal-overlay" onClick={() => setShowCampaignControlModal(false)}>
          <div className="control-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Campaign Controls</h2>
              <button className="close-btn" onClick={() => setShowCampaignControlModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="campaign-info-box">
                <h3>{selectedCampaignForControl.name}</h3>
                <span className={`status-badge ${selectedCampaignForControl.status}`}>
                  {selectedCampaignForControl.status}
                </span>
              </div>

              <form onSubmit={handleCampaignControl}>
                <div className="form-group">
                  <label>Action *</label>
                  <select
                    value={campaignControlForm.action}
                    onChange={(e) => setCampaignControlForm(prev => ({ ...prev, action: e.target.value }))}
                    required
                  >
                    <option value="">Select an action</option>
                    <option value="force_complete">Force Complete</option>
                    <option value="pause">Pause Campaign</option>
                    <option value="resume">Resume Campaign</option>
                    <option value="cancel">Cancel Campaign</option>
                    <option value="extend_deadline">Extend Deadline</option>
                  </select>
                </div>

                {campaignControlForm.action === 'extend_deadline' && (
                  <div className="form-group">
                    <label>New Deadline *</label>
                    <input
                      type="date"
                      value={campaignControlForm.new_deadline}
                      onChange={(e) => setCampaignControlForm(prev => ({ ...prev, new_deadline: e.target.value }))}
                      required
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Reason</label>
                  <textarea
                    placeholder="Reason for this action..."
                    value={campaignControlForm.reason}
                    onChange={(e) => setCampaignControlForm(prev => ({ ...prev, reason: e.target.value }))}
                    rows={3}
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowCampaignControlModal(false)}>Cancel</button>
                  <button type="submit" className="confirm-btn" disabled={submitting}>
                    {submitting ? 'Processing...' : 'Execute Action'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Refund Modal */}
      {showRefundModal && (
        <div className="modal-overlay" onClick={() => setShowRefundModal(false)}>
          <div className="refund-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Initiate Refund</h2>
              <button className="close-btn" onClick={() => setShowRefundModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <form onSubmit={handleProcessRefund}>
                <div className="form-group">
                  <label>Campaign ID *</label>
                  <input
                    type="text"
                    placeholder="Enter campaign ID"
                    value={refundForm.campaign_id}
                    onChange={(e) => setRefundForm(prev => ({ ...prev, campaign_id: e.target.value }))}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Amount (Leave empty for full refund)</label>
                  <input
                    type="number"
                    placeholder="Amount to refund"
                    value={refundForm.amount}
                    onChange={(e) => setRefundForm(prev => ({ ...prev, amount: e.target.value }))}
                  />
                </div>

                <div className="form-group">
                  <label>Reason *</label>
                  <textarea
                    placeholder="Reason for refund..."
                    value={refundForm.reason}
                    onChange={(e) => setRefundForm(prev => ({ ...prev, reason: e.target.value }))}
                    rows={3}
                    required
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowRefundModal(false)}>Cancel</button>
                  <button type="submit" className="confirm-btn" disabled={submitting}>
                    {submitting ? 'Processing...' : 'Process Refund'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* User Action Modal */}
      {showUserActionModal && selectedUserForAction && (
        <div className="modal-overlay" onClick={() => setShowUserActionModal(false)}>
          <div className="user-action-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>User Action</h2>
              <button className="close-btn" onClick={() => setShowUserActionModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="user-info-box">
                <h3>{selectedUserForAction.name}</h3>
                <span className="type-badge">{selectedUserForAction.type}</span>
                <span className="email">{selectedUserForAction.email}</span>
              </div>

              <form onSubmit={handleUserAction}>
                <div className="form-group">
                  <label>Action *</label>
                  <select
                    value={userActionForm.action}
                    onChange={(e) => setUserActionForm(prev => ({ ...prev, action: e.target.value }))}
                    required
                  >
                    <option value="">Select an action</option>
                    <option value="verify">Verify Email</option>
                    <option value="suspend">Suspend Account</option>
                    <option value="unsuspend">Unsuspend Account</option>
                    <option value="delete">Delete Account</option>
                  </select>
                </div>

                {(userActionForm.action === 'suspend' || userActionForm.action === 'delete') && (
                  <div className="form-group">
                    <label>Reason *</label>
                    <textarea
                      placeholder="Reason for this action..."
                      value={userActionForm.reason}
                      onChange={(e) => setUserActionForm(prev => ({ ...prev, reason: e.target.value }))}
                      rows={3}
                      required
                    />
                  </div>
                )}

                <div className="modal-actions">
                  <button type="button" onClick={() => setShowUserActionModal(false)}>Cancel</button>
                  <button type="submit" className="confirm-btn" disabled={submitting}>
                    {submitting ? 'Processing...' : 'Execute Action'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* ROI Detail Modal */}
      {showROIModal && selectedROICampaign && (
        <div className="modal-overlay" onClick={() => setShowROIModal(false)}>
          <div className="roi-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📊 Campaign Performance Details</h2>
              <button className="close-btn" onClick={() => setShowROIModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="roi-campaign-header">
                <h3>{selectedROICampaign.campaign_name}</h3>
                <div className="roi-meta">
                  <span className="brand">Brand: {selectedROICampaign.brand_name}</span>
                  <span className={`status-badge ${selectedROICampaign.status}`}>{selectedROICampaign.status}</span>
                  <span className="niche">{selectedROICampaign.niche}</span>
                </div>
              </div>

              <div className="roi-comparison">
                <div className="comparison-section">
                  <h4>🎯 Expected vs Actual Performance</h4>
                  <div className="comparison-grid">
                    <div className="comparison-item">
                      <span className="metric-label">Budget (escrow)</span>
                      <div className="metric-values">
                        <div className="expected">
                          <span className="label">Blocked (total)</span>
                          <span className="value">₹{selectedROICampaign.expected.budget?.toLocaleString() || 0}</span>
                        </div>
                        <div className="actual">
                          <span className="label">Used to date (gross)</span>
                          <span className="value">₹{selectedROICampaign.actual.spend?.toLocaleString() || 0}</span>
                        </div>
                        <div className="variance">
                          {selectedROICampaign.expected.budget > 0 && (
                            <span className={selectedROICampaign.actual.spend <= selectedROICampaign.expected.budget ? 'positive' : 'negative'}>
                              {Math.round((selectedROICampaign.actual.spend / selectedROICampaign.expected.budget) * 100)}% utilized
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="comparison-item">
                      <span className="metric-label">Creators</span>
                      <div className="metric-values">
                        <div className="expected">
                          <span className="label">Required</span>
                          <span className="value">{selectedROICampaign.expected.creators}</span>
                        </div>
                        <div className="actual">
                          <span className="label" title="Accepted, standby, monitoring, or completed creators">Active</span>
                          <span className="value">{selectedROICampaign.actual.creators}</span>
                        </div>
                        <div className="variance">
                          {selectedROICampaign.expected.creators > 0 && (
                            <span className={selectedROICampaign.actual.creators >= selectedROICampaign.expected.creators ? 'positive' : 'negative'}>
                              {Math.round((selectedROICampaign.actual.creators / selectedROICampaign.expected.creators) * 100)}% filled
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="comparison-item">
                      <span className="metric-label">Expected Reach</span>
                      <div className="metric-values">
                        <div className="expected">
                          <span className="label">Committed</span>
                          <span className="value">{selectedROICampaign.expected.reach?.toLocaleString() || 'N/A'}</span>
                        </div>
                        <div className="actual">
                          <span className="label">Actual (sum)</span>
                          <span className="value">
                            {selectedROICampaign.actual.reach != null && selectedROICampaign.actual.reach !== ''
                              ? Number(selectedROICampaign.actual.reach).toLocaleString()
                              : '—'}
                          </span>
                        </div>
                        <div className="variance">
                          {selectedROICampaign.expected.reach > 0 && selectedROICampaign.actual.reach > 0 && (
                            <span className={selectedROICampaign.actual.reach >= selectedROICampaign.expected.reach ? 'positive' : 'negative'}>
                              {Math.round((selectedROICampaign.actual.reach / selectedROICampaign.expected.reach) * 100)}% achieved
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="comparison-item">
                      <span className="metric-label">Expected Engagement</span>
                      <div className="metric-values">
                        <div className="expected">
                          <span className="label">Committed</span>
                          <span className="value">{selectedROICampaign.expected.engagement?.toLocaleString() || 'N/A'}</span>
                        </div>
                        <div className="actual">
                          <span className="label">Actual (sum)</span>
                          <span className="value">
                            {selectedROICampaign.actual.engagement != null && selectedROICampaign.actual.engagement !== ''
                              ? Number(selectedROICampaign.actual.engagement).toLocaleString()
                              : '—'}
                          </span>
                        </div>
                        <div className="variance">
                          {selectedROICampaign.expected.engagement > 0 && selectedROICampaign.actual.engagement > 0 && (
                            <span className={selectedROICampaign.actual.engagement >= selectedROICampaign.expected.engagement ? 'positive' : 'negative'}>
                              {Math.round((selectedROICampaign.actual.engagement / selectedROICampaign.expected.engagement) * 100)}% achieved
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="invitation-section">
                  <h4>📧 Invitation Statistics</h4>
                  <div className="invitation-stats">
                    <div className="inv-stat">
                      <span className="inv-value">{selectedROICampaign.invitations.total}</span>
                      <span className="inv-label">Total Sent</span>
                    </div>
                    <div className="inv-stat accepted" title="Includes accepted, on standby, monitoring, and completed invitations">
                      <span className="inv-value">{selectedROICampaign.invitations.accepted}</span>
                      <span className="inv-label">Active</span>
                    </div>
                    <div className="inv-stat rejected">
                      <span className="inv-value">{selectedROICampaign.invitations.rejected}</span>
                      <span className="inv-label">Rejected</span>
                    </div>
                    <div className="inv-stat pending">
                      <span className="inv-value">{selectedROICampaign.invitations.pending}</span>
                      <span className="inv-label">Pending</span>
                    </div>
                    <div className="inv-stat rate">
                      <span className="inv-value">
                        {(selectedROICampaign.invitations.responded ?? 0) > 0
                          ? `${selectedROICampaign.invitations.acceptance_rate}%`
                          : '—'}
                      </span>
                      <span className="inv-label">Acceptance rate</span>
                      <span className="inv-sublabel" style={{ fontSize: '0.7rem', color: '#64748b', display: 'block' }}>
                        of {selectedROICampaign.invitations.responded ?? 0} decided
                      </span>
                    </div>
                  </div>
                </div>

                <div className="content-section">
                  <h4>📝 Content Review Status</h4>
                  <div className="content-review-stats">
                    <div className="review-stat total">
                      <span className="count">{selectedROICampaign.content.total}</span>
                      <span className="label">Total Submissions</span>
                    </div>
                    <div className="review-stat approved">
                      <span className="count">{selectedROICampaign.content.approved}</span>
                      <span className="label">Approved</span>
                    </div>
                    <div className="review-stat rejected">
                      <span className="count">{selectedROICampaign.content.rejected}</span>
                      <span className="label">Rejected</span>
                    </div>
                    <div className="review-stat pending">
                      <span className="count">{selectedROICampaign.content.pending}</span>
                      <span className="label">Pending Review</span>
                    </div>
                  </div>
                </div>

                {selectedROICampaign.roi_score > 0 && (
                  <div className="roi-score-section">
                    <h4>📈 ROI Score</h4>
                    <div className={`roi-score ${selectedROICampaign.roi_score >= 100 ? 'excellent' : selectedROICampaign.roi_score >= 75 ? 'good' : 'needs-improvement'}`}>
                      <span className="score">{selectedROICampaign.roi_score}%</span>
                      <span className="label">
                        {selectedROICampaign.roi_score >= 100 ? 'Exceeding Expectations' : 
                         selectedROICampaign.roi_score >= 75 ? 'On Track' : 'Needs Attention'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowROIModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
